import { Component, OnInit, OnDestroy, ElementRef, ViewChild, NgZone, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { forkJoin, of, Observable, Subscription, combineLatest } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { LoanValidationService } from '../../services/loan-validation';
import { TruthManagement, EvaluationCompletionEvent } from '../../services/truth-management';
import { AuthService } from '../../services/auth.service';
import { ClientCapabilityService } from '../../services/client-capability.service';
import { ModalService } from '../../services/modal.service';
import { ValidationRun, ValidationDetail, RunSummary, DocumentKey, EvaluationFile, EvaluationStatus, EvaluationFilesResponse } from '../../models/truth-management.model';

// EvaluationFile augmented with client-side fields fetched from separate API calls
interface EvaluationFileEnriched extends EvaluationFile {
  id?: number;  // optional numeric id (index-based fallback)
  attributes?: Array<{ id?: number; uuid?: string; attribute: string; value: string }>;
}

// File group interface for validation details filtering
interface FileGroup {
  fileName: string;
  fileAccuracy: number;
  totalFields: number;
  matchedFields: number;
  fields: ValidationDetail[];
}
import { EvaluationRuns } from '../evaluation-runs/evaluation-runs';
import { PageHeaderComponent } from '../page-header/page-header.component';

@Component({
  selector: 'app-run-validation-details',
  imports: [CommonModule, FormsModule, EvaluationRuns, PageHeaderComponent],
  templateUrl: './run-validation-details.html',
  styleUrl: './run-validation-details.scss',
})
export class RunValidationDetails implements OnInit, OnDestroy {
  validationRun: ValidationRun | null = null;
  validationDetails: ValidationDetail[] = [];
  filteredDetails: ValidationDetail[] = [];
  documentCode: string = '';
  documentKeys: DocumentKey[] = [];
  loadingKeys = false;
  loadingValidationDetails = false;
  runSummary: RunSummary = {
    totalFiles: 0,
    processedFiles: 0,
    matchedFields: 0,
    unmatchedFields: 0,
    overallAccuracy: 0,
    fileCount: 0,
    uniqueAttributes: 0,
    totalAttributes: 0
  };
  
  activeTab: 'runs' | 'details' | 'results' = 'runs';
  fileNameFilter = '';
  fieldFilter = '';
  expectedValueFilter = '';
  actualValueFilter = '';
  matchFilter: 'all' | 'yes' | 'no' = 'all';
  
  // Sorting properties for validation details
  detailsSortColumn: 'fileName' | 'field' | 'expectedValue' | 'actualValue' | 'match' | 'confidence' | null = null;
  detailsSortDirection: 'asc' | 'desc' = 'asc';
  
  // Tooltip state
  tooltipVisible = false;
  tooltipContent = '';
  tooltipX = 0;
  tooltipY = 0;

  // Table field comparison modal (eye icon on Validation Details rows)
  tableModalDetail: ValidationDetail | null = null;
  
  // Validation Matrix Data - Will be populated from API
  matrixAttributes: string[] = [];
  matrixData: any[] = [];
  loadingMatrix: boolean = false;
  
  // Authoritative field names from unique_keys_field_accuracy (do not format these)
  authoritativeFieldNames: Set<string> = new Set();
  
  // Field metadata map for is_array/is_table information
  fieldMetadata: Map<string, { isArray: boolean; isTable: boolean }> = new Map();
  private readonly fieldMetadataCachePrefix = 'runValidation_fieldMetadata_';
  private isDestroyed = false;
  
  // File-based filtering
  fileGroups: FileGroup[] = [];
  selectedFileGroup: FileGroup | null = null;
  attributeAccuracy: any = {};
  overallAccuracy: number = 0;
  isAuthenticated: boolean = false;
  showStickyHeader: boolean = false;
  private lastScrollTop: number = 0;
  private scrollThreshold: number = 100; // Minimum scroll position before sticky can appear
  @ViewChild('runSummarySection') runSummarySection!: ElementRef;
  @ViewChild('tabContent') tabContent!: ElementRef;
  
  // Evaluation Files from API (enriched with attributes after load)
  evaluationFiles: EvaluationFileEnriched[] = [];
  loadingEvalFiles = false;
  
  // Evaluation Runs Tab
  documentModel: string = '';
  
  // Evaluation Status from API
  evaluationStatus: EvaluationStatus | null = null;
  latestEvaluationId: string = '';
  
  // Smooth progress tracking
  displayedProgress: number = 0;
  private targetProgress: number = 0;
  private progressAnimationFrame: any = null;
  private progressInitializedForEvaluationId: string | null = null;
  
  // Service subscriptions (replacing component polling)
  private evaluationStateSubscription?: Subscription;
  private completionSubscription?: Subscription;
  private timerSubscription?: Subscription;
  currentTimer$?: Observable<string>; // Observable for template to display live timer

  // Deduplication guard: prevents double-loading when both evaluationCompleted$ and
  // evaluationStatusChanged fire for the same evaluation in the same event cycle.
  private recentlyHandledCompletions = new Set<string>();

  // Flag to prevent matrix data from being overwritten once built from status API
  private matrixBuiltFromStatusAPI: boolean = false;

  // Guard: set to true once EvaluationRuns emits latestRunsLoaded
  // so the safety-net timeout does not fire after data already arrived.
  private latestRunsDataReceived: boolean = false;
  private latestRunsLoadTimeout: any = null;
  
  // Evaluation ID editing
  editingEvaluationId: boolean = false;
  evaluationIdInput: string = '';
  evaluationError: string | null = null;
  
  // Explicit evaluation selection state (from Past Runs / manual override)
  private explicitEvaluationIdFromQuery: string | null = null;
  
  private loanId!: number;
  private runId!: string;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private loanValidationService: LoanValidationService,
    private truthManagementService: TruthManagement,
    public authService: AuthService,
    private sanitizer: DomSanitizer,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private clientCapability: ClientCapabilityService,
  ) {}

  getBackButtonTooltip(): string {
    return this.activeTab === 'runs' ? 'Back to Evaluation' : 'Back to runs for model';
  }

  ngOnInit(): void {
    this.isAuthenticated = this.authService.hasValidToken();

    // Set documentModel synchronously from route snapshot for immediate child component init
    // This prevents 3-second timer gap in embedded EvaluationRuns on navigation
    const snapshotParams = this.route.snapshot.params;
    if (snapshotParams['documentCode']) {
      this.documentCode = snapshotParams['documentCode'];
      this.documentModel = snapshotParams['documentCode'];
    }
    if (snapshotParams['loanId']) {
      this.loanId = +snapshotParams['loanId'];
    }
    if (snapshotParams['runId']) {
      this.runId = snapshotParams['runId'];
    }

    // Restore active tab + explicit evaluation selection from query params FIRST
    // so refresh keeps the exact past-run context.
    this.route.queryParams.subscribe(queryParams => {
      const tab = queryParams['tab'];
      if (tab === 'runs' || tab === 'details' || tab === 'results') {
        this.activeTab = tab;
      }

      const evaluationId = queryParams['evaluationId'];
      this.explicitEvaluationIdFromQuery =
        evaluationId && evaluationId !== 'latest' && evaluationId !== 'Not Available'
          ? evaluationId
          : null;
      // If no tab param, keep the default 'runs' tab (for View Runs button)
    });
    
    this.route.params.subscribe(params => {
      // Clear matrix data IMMEDIATELY on route change to prevent flash of old data
      this.matrixData = [];
      this.matrixAttributes = [];
      this.loadingMatrix = false;
      
      this.loanId = +params['loanId'];
      this.runId = params['runId'];
      this.documentCode = params['documentCode'] || '';
      this.documentModel = params['documentCode'] || '';
      
      // Subscribe to timer observable for this evaluation.
      // BUG FIX: `this.runId` may be 'latest' at this point — timers are keyed by UUID.
      // Use combineLatest to reactively resolve the correct UUID from activeEvaluations$,
      // so the timer display works whether we navigated with a UUID or 'latest'.
      this.currentTimer$ = combineLatest([
        this.truthManagementService.timers$,
        this.truthManagementService.activeEvaluations$
      ]).pipe(
        map(([timers, activeEvals]) => {
          // Prefer the timer for the active run for this document model
          const active = activeEvals.get(`${this.documentCode}:all`)
                      || activeEvals.get(`${this.documentCode}:part`);
          const id = active?.evaluation_id
                  || this.latestEvaluationId
                  || this.explicitEvaluationIdFromQuery
                  || (this.runId !== 'latest' ? this.runId : undefined);
          return id ? (timers.get(id) ?? '') : '';
        })
      );
      
      this.loadValidationData();
    });
    
    // FIXED CODE:
this.evaluationStateSubscription = this.truthManagementService.activeEvaluations$.subscribe(
  activeEvals => {
    const activeEvalFull = activeEvals.get(`${this.documentCode}:all`);
    const activeEvalTest = activeEvals.get(`${this.documentCode}:part`);
    const displayedEvalId = this.getEvaluationId();

    // ── Hydration fallback ─────────────────────────────────────────────────
    // On initial load, displayedEvalId may be null/empty (runId = 'latest', no
    // explicit query param yet).  If an active run already exists in the service,
    // hydrate latestEvaluationId so downstream logic resolves the correct UUID.
    if (!displayedEvalId) {
      const knownActive = activeEvalFull || activeEvalTest;
      if (knownActive) {
        this.latestEvaluationId = knownActive.evaluation_id;
      }
      return;
    }
    // ──────────────────────────────────────────────────────────────────────

    const activeEval = (displayedEvalId && activeEvalFull?.evaluation_id === displayedEvalId
      ? activeEvalFull
      : displayedEvalId && activeEvalTest?.evaluation_id === displayedEvalId
        ? activeEvalTest
        : (activeEvalFull || activeEvalTest)); // Returns "abc-old-1" from route
    const isViewingActiveEvaluation = activeEval && activeEval.evaluation_id === displayedEvalId;
    
    if (isViewingActiveEvaluation && activeEval.status) {
      // Only update if user is viewing the ACTIVE evaluation (xyz-new-2)
      this.evaluationStatus = activeEval.status;
      this.updateProgressFromStatus(activeEval.status);
      this.updateRunSummaryFromStatus(activeEval.status);
      this.cdr.detectChanges();
    } else if (activeEval && !isViewingActiveEvaluation) {
      // User clicked old evaluation (abc-old-1), new one (xyz-new-2) is running
      // DO NOT UPDATE - Keep showing abc-old-1 data
      }
    }
  );

    // ── Parent-level evaluationCompleted$ subscription ───────────────────────
    // This subscription lives on the PARENT component so it survives tab switches.
    // <app-evaluation-runs> is @if (activeTab === 'runs') — Angular DESTROYS it when
    // the user is on Details or Results, cancelling any subscription inside it.
    // By subscribing here we always catch the completion signal regardless of which
    // tab is currently active.
    this.completionSubscription = this.truthManagementService.evaluationCompleted$
      .pipe(filter((event: EvaluationCompletionEvent) => event.documentModel === this.documentCode))
      .subscribe((event: EvaluationCompletionEvent) => {
        // Deduplication: evaluationStatusChanged (from evaluation-runs) and evaluationCompleted$
        // may both fire for the same evaluation when the Runs tab is active.
        if (this.recentlyHandledCompletions.has(event.evaluationId)) { return; }
        this.recentlyHandledCompletions.add(event.evaluationId);
        setTimeout(() => this.recentlyHandledCompletions.delete(event.evaluationId), 5000);
        this.loadCompletedEvaluationResults(event.evaluationId);
      });
  }

  onTabContentScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const currentScrollTop = target.scrollTop;
    
    // Only show sticky header after scrolling past the threshold (run summary area)
    if (currentScrollTop < this.scrollThreshold) {
      // At the top - hide sticky header
      this.showStickyHeader = false;
    } else {
      // Past the threshold - show sticky header (stays visible while scrolling up/down)
      this.showStickyHeader = true;
    }
    
    this.lastScrollTop = currentScrollTop;
  }

  private getEffectiveEvaluationId(): string | null {
    // Query-param selection is explicit and must win over latest fallback.
    if (this.explicitEvaluationIdFromQuery && this.explicitEvaluationIdFromQuery !== 'Not Available') {
      return this.explicitEvaluationIdFromQuery;
    }

    // Direct navigation with explicit run ID should also be treated as explicit context.
    if (this.runId && this.runId !== 'latest' && this.runId !== 'Not Available') {
      return this.runId;
    }

    return null;
  }

  private syncEvaluationSelectionInUrl(evaluationId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { evaluationId },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });
  }

  loadValidationData(): void {
    // Reset local progress state; first incoming status will snap to backend progress.
    this.displayedProgress = 0;
    this.targetProgress = 0;
    this.progressInitializedForEvaluationId = null;
    
    // Reset flag to allow matrix to be built fresh
    this.matrixBuiltFromStatusAPI = false;
    
    // Clear stale data to prevent flash of previous evaluation data
    this.matrixData = [];
    this.matrixAttributes = [];
    this.authoritativeFieldNames.clear();
    this.validationRun = null;
    this.evaluationStatus = null;
    this.loadingMatrix = false;

    // Explicit evaluation selection (from Past Runs/manual selection) always has top priority.
    const effectiveEvaluationId = this.getEffectiveEvaluationId();
    if (effectiveEvaluationId) {
      this.runId = effectiveEvaluationId;
      this.latestEvaluationId = effectiveEvaluationId;
    }

    // ── Active-eval-first init ───────────────────────────────────────────────
    // Before making any API call, check if the service already knows about a
    // running/pending evaluation for this document model.  The BehaviorSubject is
    // read synchronously — zero HTTP, zero async latency.
    // Covers both: run started from this page AND run started from loan-validation.
    if (this.runId === 'latest' && !effectiveEvaluationId) {
      const activeEval = this.truthManagementService.getActiveEvaluationForModel(this.documentCode);
      if (activeEval) {
        // Service already has the state — populate directly without any API call.
        this.latestEvaluationId = activeEval.evaluation_id;
        this.runId = activeEval.evaluation_id;
        // Build a minimal validationRun so the template can render immediately.
        this.validationRun = {
          runId: activeEval.evaluation_id,
          evaluationId: activeEval.evaluation_id,
          loanId: this.loanId,
          loanNumber: this.documentCode,
          documentCode: this.documentCode,
          accuracy: 0,
          status: activeEval.status?.status ?? 'RUNNING',
          createdDate: activeEval.started_at ? new Date(activeEval.started_at.endsWith('Z') ? activeEval.started_at : activeEval.started_at + 'Z') : new Date(),
          totalFiles: activeEval.file_count ?? 0,
          completedFiles: activeEval.status?.completed_files ?? 0,
          failedFiles: activeEval.status?.failed_files ?? 0,
          duration: '—',
          durationMs: 0,
          attributeCount: 0,
          ocrMode: activeEval.status?.ocr_mode || undefined,
          llmModel: activeEval.status?.llm_model || undefined
        };
        if (activeEval.status) {
          this.evaluationStatus = activeEval.status;
          this.updateProgressFromStatus(activeEval.status);
          this.updateRunSummaryFromStatus(activeEval.status);
        }
        // Cancel the safety-net timeout — we already have data.
        clearTimeout(this.latestRunsLoadTimeout);
        this.latestRunsDataReceived = true;
        // Load document keys for the matrix column headers (lightweight, cached).
        if (this.documentCode && !this.loadingKeys) {
          this.loadDocumentKeysFromAPI();
        }
        this.cdr.detectChanges();
        return; // activeEvaluations$ subscription + completionSubscription handle the rest
      }
    }

    // If runId is "latest", get the most recent evaluation from past runs
    if (this.runId === 'latest') {
      // When the default Runs tab is active, the embedded EvaluationRuns component will
      // call loadBothRunTypes() and emit latestRunsLoaded — use that data instead of
      // making a redundant getEvaluationRuns() call here.
      if (this.activeTab === 'runs') {
        this.latestRunsDataReceived = false;
        // Safety net: if EvaluationRuns fails to emit within 5 s, fall back to direct API call.
        clearTimeout(this.latestRunsLoadTimeout);
        this.latestRunsLoadTimeout = setTimeout(() => {
          if (!this.latestRunsDataReceived) {
            this.loadLatestFromPastRuns();
          }
        }, 5000);
        return;
      }
      this.loadLatestFromPastRuns();
      return;
    }
    
    // Try to load validation run from service first
    this.loanValidationService.getValidationRunById(this.runId).subscribe(run => {
      if (run) {
        this.validationRun = run;
        // Use the actual evaluation ID if available
        (this.validationRun as any).evaluationId = this.runId;
        // Trigger change detection to update UI
        this.cdr.detectChanges();
        // Always load validation details after setting the run
        this.loadValidationDetailsFromAPI();
      }
      // Cache miss: do NOT call loadFromAPI() here — fetchLatestEvaluationStatus() below
      // already calls GET /eval/status/{id} and will populate validationRun when null.
    });

    // Load document keys from API if document code is provided
    if (this.documentCode) {
      this.loadDocumentKeysFromAPI();
      // NOTE: Do NOT call loadEvaluationFiles() here - it causes flicker!
      // The status API (fetchLatestEvaluationStatus) is the source of truth for matrix data.
      // Evaluation files will be loaded as a fallback ONLY if status API fails.
      // NOTE: updateRunSummaryFromActualData() removed — fetchLatestEvaluationStatus() below
      // calls updateRunSummaryFromStatus() which is the same data from the same endpoint.
    }
    // Note: loadValidationDetailsFromAPI is called inside the subscribe callback above

    // Run summary is calculated from actual validation details in updateRunSummaryFromValidationDetails()
    
    // Fetch latest evaluation status for run summary
    this.fetchLatestEvaluationStatus();
    
    // Note: Polling is now handled by TruthManagement service
    // Component subscribes to activeEvaluations$ and timers$ observables in ngOnInit
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    // Unsubscribe from service observables
    this.evaluationStateSubscription?.unsubscribe();
    this.completionSubscription?.unsubscribe();
    this.timerSubscription?.unsubscribe();

    // Clean up progress animation
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
    }

    // Clean up safety-net timeout from loadValidationData()
    clearTimeout(this.latestRunsLoadTimeout);
  }

  /**
   * Component-level polling removed - using centralized TruthManagement service
   * The service handles all polling, status updates, and live timer management.
   * Component subscribes to activeEvaluations$ and timers$ observables.
   */

  /**
   * Get the evaluation ID to use for polling
   */
  private getEvaluationIdForPolling(): string | null {
    // Explicitly selected evaluation must always win (clicked run / query-param).
    const selectedEvaluationId = this.getEffectiveEvaluationId();
    if (selectedEvaluationId) {
      return selectedEvaluationId;
    }
    
    // Fall back to runId if it's a valid UUID
    if (this.runId && this.runId !== 'latest' && this.runId !== 'Not Available') {
      return this.runId;
    }
    
    // Try latestEvaluationId
    if (this.latestEvaluationId && this.latestEvaluationId !== 'Not Available') {
      return this.latestEvaluationId;
    }
    
    return null;
  }

  /**
   * Load the latest evaluation from past runs using API
   */
  loadLatestFromPastRuns(): void {
    if (!this.documentCode) {
       this.createPlaceholderRun();
      return;
    }

    // Mirror exactly what evaluation-runs component does: fetch BOTH typed endpoints
    // so the evaluation_id used for eval/status/{id} always comes from the same source
    // as the badge display (run_type=all for eval runs, run_type=part for test runs).
    forkJoin({
      evalRuns: this.truthManagementService.getEvaluationRuns(this.documentCode, 'all'),
      testRuns: this.truthManagementService.getEvaluationRuns(this.documentCode, 'part')
    }).subscribe({
      next: ({ evalRuns, testRuns }) => {
        const latestEvalRun = evalRuns?.runs?.[0];
        const latestTestRun = testRuns?.runs?.[0];

        // Pick the most recently started run across both types
        let latestRun = latestEvalRun;
        if (latestTestRun && latestEvalRun) {
          const evalDate = latestEvalRun.started_at ? new Date(latestEvalRun.started_at) : new Date(0);
          const testDate = latestTestRun.started_at ? new Date(latestTestRun.started_at) : new Date(0);
          latestRun = testDate > evalDate ? latestTestRun : latestEvalRun;
        } else if (latestTestRun) {
          latestRun = latestTestRun;
        }

        if (!latestRun) {
          this.createPlaceholderRun();
          return;
        }

        // Parse accuracy - handle both number and string formats
        let accuracy = 0;
        if (typeof latestRun.overall_accuracy === 'number') {
          accuracy = latestRun.overall_accuracy;
        } else if (typeof latestRun.overall_accuracy === 'string') {
          accuracy = parseFloat(latestRun.overall_accuracy.replace('%', '')) || 0;
        }

        // Parse date
        let createdDate: Date;
        if (latestRun.started_at) {
          const dateStr = latestRun.started_at.endsWith('Z') ? latestRun.started_at : latestRun.started_at + 'Z';
          createdDate = new Date(dateStr);
        } else {
          createdDate = new Date();
        }

        // Calculate duration - only for completed evaluations
        // For running evaluations, the live timer will update the duration
        const isRunning = latestRun.status === 'RUNNING' || latestRun.status === 'PENDING';
        const duration = isRunning ? undefined : this.calculateDuration(latestRun.time_taken_seconds, latestRun.started_at, latestRun.completed_at);

        // Create validation run from API response
        this.validationRun = {
          runId: latestRun.evaluation_id,
          evaluationId: latestRun.evaluation_id,
          loanId: 0,
          loanNumber: this.documentCode,
          documentCode: this.documentCode,
          accuracy: accuracy,
          status: latestRun.status === 'COMPLETED' ? 'Completed' : latestRun.status,
          createdDate: createdDate,
          totalFiles: latestRun.total_files,
          completedFiles: latestRun.completed_files,
          failedFiles: latestRun.failed_files,
          duration: duration,
          ocrMode: latestRun.ocr_mode || undefined,
          llmModel: latestRun.llm_model || undefined
        };

        // Service handles timer automatically - no need to start/stop manually

        // Update runId to the actual evaluation ID
        this.runId = latestRun.evaluation_id;

        // Update run summary with initial data (accuracy will be updated by fetchLatestEvaluationStatus)
        this.runSummary = {
          fileCount: latestRun.completed_files || 0,
          totalFiles: latestRun.total_files || 0,
          processedFiles: latestRun.completed_files || 0,
          matchedFields: 0,
          unmatchedFields: 0,
          overallAccuracy: 0,
          uniqueAttributes: 0,
          totalAttributes: 0
        };

        // Fetch full eval/status/{evaluation_id} — the runs list only provides summary
        // fields and does not include results[] or details[] needed for the matrix and
        // Details tab. The evaluation_id always comes from the typed runs endpoints above.
        this.fetchLatestEvaluationStatus();

        // loadDocumentKeysFromAPI is still needed for matrix attribute labels.
        // loadValidationDetailsFromAPI and loadEvaluationFiles are handled internally
        // by fetchLatestEvaluationStatus() — calling them here would be redundant.
        if (this.documentCode) {
          this.loadDocumentKeysFromAPI();
        }
      },
      error: (error) => {
        this.createPlaceholderRun();
      }
    });
  }

  /**
   * Load validation run from API if not found in service
   */
  loadFromAPI(): void {
    // Get the evaluation status from API
    this.truthManagementService.getEvaluationStatus(this.runId).subscribe({
      next: (status: any) => {
       // Calculate accuracy from the status (overall_accuracy is 0-1 scale, multiply by 100)
        let accuracy = 0;
        if (status.summary?.overall_accuracy !== undefined) {
          accuracy = status.summary.overall_accuracy * 100;
        }
        
        // Parse date
        let createdDate: Date;
        if (status.started_at) {
          const dateStr = status.started_at.endsWith('Z') ? status.started_at : status.started_at + 'Z';
          createdDate = new Date(dateStr);
        } else {
          createdDate = new Date();
        }
        
        // Calculate duration - only for completed evaluations
        // For running evaluations, the live timer will update the duration
        const isRunning = status.status === 'RUNNING' || status.status === 'PENDING';
        const duration = isRunning ? undefined : this.calculateDuration(status.time_taken_seconds, status.started_at, status.completed_at);
        
        this.validationRun = {
          runId: status.evaluation_id || this.runId,
          evaluationId: status.evaluation_id || this.runId,
          loanId: this.loanId,
          loanNumber: status.document_model || this.documentCode,
          documentCode: status.document_model || this.documentCode,
          accuracy: accuracy,
          status: status.status === 'COMPLETED' ? 'Completed' : status.status,
          createdDate: createdDate,
          totalFiles: status.total_files,
          completedFiles: status.completed_files,
          failedFiles: status.failed_files,
          duration: duration,
          ocrMode: status.ocr_mode || undefined,
          llmModel: status.llm_model || undefined
        };

        // Service handles timer automatically - no need to start/stop manually

       // Trigger change detection to update the UI immediately
        this.cdr.detectChanges();
        
        // Update document code if we got it from the API
        if (status.document_model && !this.documentCode) {
          this.documentCode = status.document_model;
          this.documentModel = status.document_model;
        }
        
        // Load document keys and validation details after we have the validation run
        if (this.documentCode) {
          this.loadDocumentKeysFromAPI();
        }
        // Load validation details — pass status to avoid an extra eval/status HTTP call
        this.loadValidationDetailsFromAPI(undefined, status);
      },
      error: (error) => {
        this.createPlaceholderRun();
      }
    });
  }

  /**
   * Create a placeholder run when no data is available
   */
  createPlaceholderRun(): void {
   this.validationRun = {
      runId: this.runId,
      evaluationId: this.runId,
      loanId: this.loanId,
      loanNumber: this.documentCode,
      documentCode: this.documentCode,
      accuracy: 0,
      status: 'Completed',
      createdDate: new Date(),
      duration: ''
    };
  }

  /**
   * Update run summary from actual evaluation data (API only)
   */
  updateRunSummaryFromActualData(): void {
    // If we have the runId, get evaluation status from API
    if (this.runId && this.runId !== 'latest') {
      this.truthManagementService.getEvaluationStatus(this.runId).subscribe({
        next: (status: any) => {
         // Calculate accuracy from the status (overall_accuracy is 0-1 scale, multiply by 100)
          let accuracy = 0;
          if (status.summary?.overall_accuracy !== undefined) {
            accuracy = status.summary.overall_accuracy * 100;
          }
          
          // Update run summary with API data
          this.runSummary = {
            fileCount: status.completed_files || 0,
            totalFiles: status.total_files || 0,
            processedFiles: status.completed_files || 0,
            matchedFields: 0, // Will be calculated from validation details
            unmatchedFields: 0, // Will be calculated from validation details
            overallAccuracy: accuracy,
            uniqueAttributes: 0,
            totalAttributes: 0
          };
          
          // Update validation run with real accuracy
          if (this.validationRun) {
            this.validationRun.accuracy = accuracy;
            (this.validationRun as any).evaluationId = status.evaluation_id || this.runId;
          }
        },
        error: (error) => {
          // Fallback to loading evaluation files
          this.loadEvaluationFiles();
        }
      });
    } else {
      // For latest runs, just load evaluation files
      this.loadEvaluationFiles();
    }
  }

  /**
   * Update run summary from evaluation files (fallback method)
   */
  updateRunSummaryFromEvaluationFiles(): void {
    if (!this.documentCode) {
      return;
    }
    
    this.truthManagementService.getEvaluationFiles(this.documentCode).subscribe({
      next: (evalFiles) => {
       // Calculate summary statistics
        let totalMatched = 0;
        let totalUnmatched = 0;
        let totalAccuracy = 0;
        
        evalFiles.forEach(file => {
          // Calculate based on file properties that exist
          // For now, assume all files contribute equally to accuracy
          totalMatched += 1; // This should be based on actual file validation results
        });
        
        const averageAccuracy = evalFiles.length > 0 ? totalAccuracy / evalFiles.length : 0;
        
        // Update run summary
        this.runSummary = {
          fileCount: evalFiles.length,
          totalFiles: evalFiles.length,
          processedFiles: evalFiles.length,
          matchedFields: totalMatched,
          unmatchedFields: totalUnmatched,
          overallAccuracy: averageAccuracy,
          uniqueAttributes: 0,
          totalAttributes: 0
        };
        
        // Update validation run with latest data
        if (this.validationRun) {
          this.validationRun.accuracy = averageAccuracy;
          this.validationRun.createdDate = new Date();
        }
        },
      error: (error) => {
      }
    });
  }

  /**
   * Load evaluation files from Swagger API
   * NOTE: This is a FALLBACK method. The status API is the primary source of truth.
   */
  loadEvaluationFiles(): void {
    // Skip if matrix already built from status API (prevents flicker)
    if (this.matrixBuiltFromStatusAPI) {
      return;
    }
    this.loadingEvalFiles = true;
    
    this.truthManagementService.getEvaluationFiles(this.documentCode).subscribe({
      next: (rawFiles) => {
        const evalFiles = rawFiles as EvaluationFileEnriched[];
        // RE-CHECK flag inside callback - status API may have completed while waiting
        if (this.matrixBuiltFromStatusAPI) {
          this.loadingEvalFiles = false;
          return;
        }
        // The API returns files but without their attributes (ground truth)
        // We need to fetch attributes separately for each file using their UUID
        if (evalFiles.length > 0) {
          // Log files and their UUIDs
          evalFiles.forEach((f, i) => {
          });
          
          // Create array of requests to get attributes for each file using UUID
          const attributeRequests = evalFiles.map(file => {
            const fileUuid = file.uuid;
            if (fileUuid) {
              return this.truthManagementService.getTruthValuesByFileId(fileUuid).pipe(
                map(response => response.attributes), // Extract attributes from AttributesResponse
                catchError(err => {
                  return of([]);
                })
              );
            } else {
              return of([]);
            }
          });
          
          // Fetch all attributes in parallel
          forkJoin(attributeRequests).subscribe({
            next: (allAttributes) => {
              // Enrich evaluation files with their attributes
              evalFiles.forEach((file, index) => {
                const attrs = allAttributes[index] || [];
                // Convert TruthValue[] to attribute format
                file.attributes = attrs.map((tv: any) => ({
                  id: tv.id,
                  uuid: tv.uuid,
                  attribute: tv.attributeName || tv.attribute || '',
                  value: tv.value || ''
                }));
               });
              
              // Store enriched files
              this.evaluationFiles = evalFiles;
              
              // Log total attributes
              const totalAttrs = evalFiles.reduce((sum, f) => sum + (f.attributes?.length || 0), 0);
              if (totalAttrs > 0 && evalFiles[0]?.attributes?.length) {
              }
              
              // Note: Matrix data is built from status API only (buildMatrixFromStatusResults)
              // Do NOT call updateMatrixWithEvaluationFiles here - it causes flicker
              
              // NOW load validation details - after evaluation files with attributes are available
             this.loadValidationDetailsFromAPI();
              
              this.loadingEvalFiles = false;
            },
            error: (error) => {
              this.evaluationFiles = evalFiles;
              this.loadValidationDetailsFromAPI();
              this.loadingEvalFiles = false;
            }
          });
        } else {
         this.evaluationFiles = evalFiles;
          this.loadValidationDetailsFromAPI();
          this.loadingEvalFiles = false;
        }
      },
      error: (error) => {
        // Still try to load validation details even if eval files fail
        this.loadValidationDetailsFromAPI();
        this.loadingEvalFiles = false;
      }
    });
  }

  /**
   * Update matrix data with files from evaluation API
   */
  updateMatrixWithEvaluationFiles(evalFiles: EvaluationFileEnriched[]): void {
    // Don't overwrite matrix if already built from status API
    if (this.matrixBuiltFromStatusAPI) {
      return;
    }
    if (this.matrixAttributes.length === 0) {
      return;
    }
    
    // Create new matrix data entries for each evaluation file
    const newMatrixData = evalFiles.map((file, index) => {
      // Create attributes object with default 'Y' values
      const attributes: any = {};
      this.matrixAttributes.forEach(attr => {
        attributes[attr] = 'Y'; // Default to matched, actual data should come from API
      });
      
      return {
        id: file.id || index + 1,
        fileName: file.file_name,
        accuracy: 100.0, // Default, will be calculated from actual validation results
        attributes: attributes
      };
    });
    
    // Update matrix data
    if (newMatrixData.length > 0) {
      this.matrixData = newMatrixData;
     // Calculate attribute accuracy after matrix is populated
      this.calculateAttributeAccuracy();
    } else {
    }
  }

  loadDocumentKeysFromAPI(): void {
     this.loadingKeys = true;
    this.hydrateFieldMetadataFromCache();
    
    this.truthManagementService.getDocumentKeys(this.documentCode).subscribe({
      next: (response) => {
        this.documentKeys = response.keys.filter(k => k.is_active);
        
        // Build field metadata map from document keys
        this.fieldMetadata.clear();
        this.documentKeys.forEach(key => {
          // Store metadata with both key_label and key_name (handles underscore/space variations)
          const isTable = key.feature === 'table';
          this.fieldMetadata.set(key.key_label, { isArray: key.is_array || false, isTable });
          if (key.key_name !== key.key_label) {
            this.fieldMetadata.set(key.key_name, { isArray: key.is_array || false, isTable });
          }
        });
        this.persistFieldMetadataCache();
        
        // Update matrix attributes from API - but NOT if already built from status API
        if (this.documentKeys.length > 0 && !this.matrixBuiltFromStatusAPI) {
           // Use API keys for matrix attributes
          this.matrixAttributes = this.documentKeys.map(k => k.key_label);
         // Note: Matrix data is built from status API only (buildMatrixFromStatusResults)
          // Do NOT call updateMatrixWithEvaluationFiles here - it causes flicker
        }
        
        this.loadingKeys = false;
        this.triggerMatrixHeaderRefresh();
      },
      error: (err) => {
        this.loadingKeys = false;
        this.triggerMatrixHeaderRefresh();
      }
    });
  }

  private getFieldMetadataCacheKey(): string | null {
    const model = (
      this.documentCode ||
      this.documentModel ||
      this.validationRun?.documentCode ||
      ''
    ).trim();

    if (!model) {
      return null;
    }

    return `${this.fieldMetadataCachePrefix}${model.toLowerCase()}`;
  }

  private hydrateFieldMetadataFromCache(): void {
    const cacheKey = this.getFieldMetadataCacheKey();
    if (!cacheKey) {
      return;
    }

    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) {
      return;
    }

    try {
      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed)) {
        return;
      }

      const restoredEntries = new Map<string, { isArray: boolean; isTable: boolean }>();
      parsed.forEach((entry: any) => {
        if (!Array.isArray(entry) || entry.length !== 2 || typeof entry[0] !== 'string') {
          return;
        }

        restoredEntries.set(entry[0], {
          isArray: !!entry[1]?.isArray,
          isTable: !!entry[1]?.isTable
        });
      });

      if (restoredEntries.size > 0) {
        this.fieldMetadata.clear();
        restoredEntries.forEach((value, key) => this.fieldMetadata.set(key, value));
        this.triggerMatrixHeaderRefresh();
      }
    } catch (error) {
      sessionStorage.removeItem(cacheKey);
    }
  }

  private persistFieldMetadataCache(): void {
    const cacheKey = this.getFieldMetadataCacheKey();
    if (!cacheKey) {
      return;
    }

    try {
      const payload = JSON.stringify(Array.from(this.fieldMetadata.entries()));
      sessionStorage.setItem(cacheKey, payload);
    } catch (error) {
    }
  }

  private triggerMatrixHeaderRefresh(): void {
    if (this.isDestroyed) {
      return;
    }

    queueMicrotask(() => {
      if (this.isDestroyed) {
        return;
      }

      try {
        this.cdr.detectChanges();
      } catch {
        // No-op: detectChanges can throw if the view is mid-teardown.
      }
    });
  }

  sortDetailsBy(column: 'fileName' | 'field' | 'expectedValue' | 'actualValue' | 'match' | 'confidence'): void {
    if (this.detailsSortColumn === column) {
      this.detailsSortDirection = this.detailsSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.detailsSortColumn = column;
      this.detailsSortDirection = 'asc';
    }
    this.applyDetailsSorting();
  }

  /**
   * Improved natural sort for file names with GUIDs, underscores, and numbers
   * Handles: "file2.pdf" < "file15.pdf", "ASSETS-GIFT LETTER.pdf" < "ASSETS-GIFT LETTER_<GUID>.pdf"
   */
  private naturalSort(a: string, b: string): number {
    // Normalize the strings for comparison
    const aNorm = a.toLowerCase();
    const bNorm = b.toLowerCase();
    
    // Use localeCompare with numeric option for natural sorting
    // This handles numbers within strings correctly
    return aNorm.localeCompare(bNorm, undefined, { 
      numeric: true, 
      sensitivity: 'base' 
    });
  }

  /**
   * Enhanced smart value comparison with strict type hierarchy and proper detection
   * Type hierarchy: Numbers < Dates < Currency < Phone Numbers < Text
   */
  private smartValueSort(a: string, b: string): number {
    // Handle empty/null values - always sort to end
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    
    const aTrim = a.trim();
    const bTrim = b.trim();
    
    // Detect and extract data types
    const detectType = (str: string): { type: string; value: any } => {
      // 1. Currency detection: $X,XXX.XX or $X.XX
      const currencyMatch = str.match(/^\$\s*([\d,]+\.?\d*)$/);
      if (currencyMatch) {
        return { 
          type: 'currency', 
          value: parseFloat(currencyMatch[1].replace(/,/g, '')) 
        };
      }
      
      // 2. Phone number detection: (XXX) XXX-XXXX or XXX . XXX XXXX
      const phoneMatch = str.match(/^[\(\)\d\s\.\-]+$/);
      const digits = str.replace(/\D/g, '');
      if (phoneMatch && digits.length >= 10) {
        return { 
          type: 'phone', 
          value: parseInt(digits, 10) 
        };
      }
      
      // 3. Date detection: XX-XX-XXXX or XX/XX/XXXX
      const dateMatch = str.match(/^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})$/);
      if (dateMatch) {
        // Convert to timestamp for comparison
        const month = parseInt(dateMatch[1], 10);
        const day = parseInt(dateMatch[2], 10);
        const year = parseInt(dateMatch[3], 10);
        return { 
          type: 'date', 
          value: new Date(year, month - 1, day).getTime() 
        };
      }
      
      // 4. Plain number detection: XXX.XX or -XXX.XX (including ranges like "- 300.00 - 470.00")
      const numberMatch = str.match(/^-?\s*(\d+(?:[.,]\d+)?)/);
      if (numberMatch && /^[\d\s\.,\-]+$/.test(str)) {
        return { 
          type: 'number', 
          value: parseFloat(numberMatch[1].replace(/,/g, '')) 
        };
      }
      
      // 5. Everything else is text
      return { 
        type: 'text', 
        value: str.toLowerCase() 
      };
    };
    
    const aData = detectType(aTrim);
    const bData = detectType(bTrim);
    
    // Define type hierarchy order
    const typeOrder: { [key: string]: number } = {
      'number': 1,
      'date': 2,
      'currency': 3,
      'phone': 4,
      'text': 5
    };
    
    // If different types, sort by type hierarchy
    if (aData.type !== bData.type) {
      return typeOrder[aData.type] - typeOrder[bData.type];
    }
    
    // Same type - compare values
    if (aData.type === 'text') {
      return (aData.value as string).localeCompare(bData.value as string, undefined, { 
        numeric: true, 
        sensitivity: 'base' 
      });
    } else {
      // Numeric comparison for numbers, dates, currency, phone
      return (aData.value as number) - (bData.value as number);
    }
  }
  
  /**
   * Extract numeric value from confidence (handles both number and string "XX%")
   */
  private getConfidenceValue(confidence: any): number {
    if (typeof confidence === 'number') {
      return confidence;
    }
    if (typeof confidence === 'string') {
      const match = confidence.match(/(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    }
    return 0;
  }

  applyDetailsSorting(): void {
    if (!this.detailsSortColumn) return;
    
    this.filteredDetails.sort((a, b) => {
      let compareValue = 0;
      
      switch (this.detailsSortColumn) {
        case 'fileName':
          compareValue = this.naturalSort(a.fileName, b.fileName);
          break;
        case 'field':
          compareValue = a.field.localeCompare(b.field, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
          break;
        case 'expectedValue':
          compareValue = this.smartValueSort(a.expectedValue, b.expectedValue);
          break;
        case 'actualValue':
          compareValue = this.smartValueSort(a.actualValue, b.actualValue);
          break;
        case 'match':
          compareValue = (a.match === b.match) ? 0 : (a.match ? -1 : 1);
          break;
        case 'confidence':
          compareValue = this.getConfidenceValue(a.confidence) - this.getConfidenceValue(b.confidence);
          break;
      }
      
      return this.detailsSortDirection === 'asc' ? compareValue : -compareValue;
    });
  }

  getDetailsSortIcon(column: 'fileName' | 'field' | 'expectedValue' | 'actualValue' | 'match' | 'confidence'): SafeHtml {
    if (this.detailsSortColumn !== column) {
      return this.sanitizer.bypassSecurityTrustHtml('<div style="display:flex;flex-direction:column;align-items:center;line-height:0.8;font-family:Arial,sans-serif"><span>▲</span><span>▼</span></div>');
    }
    return this.sanitizer.bypassSecurityTrustHtml(this.detailsSortDirection === 'asc' ? '▲' : '▼');
  }

  applyFilter(): void {
    this.filteredDetails = this.validationDetails.filter(detail => {
      const fileNameMatch = !this.fileNameFilter || 
        detail.fileName.toLowerCase().includes(this.fileNameFilter.toLowerCase());
      
      const fieldMatch = !this.fieldFilter || 
        detail.field.toLowerCase().includes(this.fieldFilter.toLowerCase());
      
      const expectedValueMatch = !this.expectedValueFilter || 
        detail.expectedValue.toLowerCase().includes(this.expectedValueFilter.toLowerCase());
      
      const actualValueMatch = !this.actualValueFilter || 
        detail.actualValue.toLowerCase().includes(this.actualValueFilter.toLowerCase());
      
      const matchStatusMatch = this.matchFilter === 'all' || 
        (this.matchFilter === 'yes' && detail.match) ||
        (this.matchFilter === 'no' && !detail.match);
      
      return fileNameMatch && fieldMatch && expectedValueMatch && actualValueMatch && matchStatusMatch;
    });
    
    this.applyDetailsSorting();
  }

  onPasteFilterInput(
    event: ClipboardEvent,
    field: 'fileNameFilter' | 'fieldFilter' | 'expectedValueFilter' | 'actualValueFilter'
  ): void {
    event.preventDefault();
    const normalized = (event.clipboardData?.getData('text') || '').trim();

    switch (field) {
      case 'fileNameFilter':
        this.fileNameFilter = normalized;
        break;
      case 'fieldFilter':
        this.fieldFilter = normalized;
        break;
      case 'expectedValueFilter':
        this.expectedValueFilter = normalized;
        break;
      case 'actualValueFilter':
        this.actualValueFilter = normalized;
        break;
    }

    this.applyFilter();
  }

  clearFilter(): void {
    this.fileNameFilter = '';
    this.fieldFilter = '';
    this.expectedValueFilter = '';
    this.actualValueFilter = '';
    this.matchFilter = 'all';

    // Clear file selection and apply general filter
    this.clearFileSelection();
    this.applyFilter();
  }

  /**
   * Open the table comparison modal (eye icon) for a table-type validation detail row
   */
  openTableComparisonModal(detail: ValidationDetail): void {
    this.tableModalDetail = detail;
  }

  closeTableComparisonModal(): void {
    this.tableModalDetail = null;
  }

  /**
   * Calculate accuracy percentage for each attribute across all files
   * LEGACY: Only used for loadMatrixForEvaluation fallback path
   * The main buildMatrixFromStatusResults path gets accuracy directly from API
   */
  calculateAttributeAccuracy(): void {
    if (this.matrixData.length === 0 || this.matrixAttributes.length === 0) {
     return;
    }

    // Skip if already populated from API (prevents overwriting backend-calculated accuracy)
    if (this.matrixBuiltFromStatusAPI && Object.keys(this.attributeAccuracy).length > 0) {
    return;
    }  const totalFiles = this.matrixData.length;
    this.attributeAccuracy = {};
    let totalMatches = 0;
    let totalAttributes = 0;

    // Only calculate accuracy for displayed fields (filters out duplicate unnumbered fields)
    this.matrixAttributes.forEach(attr => {
      // Skip hidden duplicate fields and malformed names
      if (!this.shouldDisplayField(attr)) {
        return;
      }
      
      let matchCount = 0;
      let totalCount = 0;
      let hasAnyData = false; // Track if ANY file has validation data for this field
      
      this.matrixData.forEach(file => {
        const fieldValue = file.attributes ? file.attributes[attr] : undefined;
        
        // Check if field has validation data (not undefined, not empty, not '-')
        if (fieldValue && fieldValue !== '-') {
          hasAnyData = true;
        }
        
        if (fieldValue === 'Y') {
          matchCount++;
          totalCount++;
        } else if (fieldValue === 'N') {
          totalCount++;
        }
      });
      
      // If no validation data exists for this field, show 0% (likely ground truth added after validation run)
      if (!hasAnyData || totalCount === 0) {
        this.attributeAccuracy[attr] = 0;
      } else {
        const accuracy = (matchCount / totalCount) * 100;
        this.attributeAccuracy[attr] = parseFloat(accuracy.toFixed(1));
      }
      
      totalMatches += matchCount;
      totalAttributes += totalCount; // Only count files that have data
    });

    // Priority: Use status API overall_accuracy (0-1 scale * 100), fallback to calculated
    if (this.evaluationStatus?.summary?.overall_accuracy !== undefined) {
      this.overallAccuracy = this.evaluationStatus.summary.overall_accuracy * 100;
       } else {
      // Calculate overall accuracy from matrix data as fallback
      this.overallAccuracy = totalAttributes > 0 
        ? parseFloat(((totalMatches / totalAttributes) * 100).toFixed(1))
        : 0;
    }
 }

  /**
   * Calculate row accuracy from matrix-visible values only.
   * Uses Y/(Y+N) and ignores blank/unknown values.
   */
  private calculateMatrixRowAccuracy(attributes: { [key: string]: string }): number {
    const values = Object.values(attributes || {});
    const yCount = values.filter(v => v === 'Y').length;
    const nCount = values.filter(v => v === 'N').length;
    const evaluated = yCount + nCount;

    return evaluated > 0 ? (yCount / evaluated) * 100 : 0;
  }

  /**
   * Update matrix data with actual Y/N values from validation details
   * This replaces the default 'Y' values with actual match results
   */
  updateMatrixFromValidationDetails(validationDetails: ValidationDetail[]): void {
    if (!validationDetails || validationDetails.length === 0) {
     return;
    }
    // Group validation details by file name
    const fileDetailsMap = new Map<string, ValidationDetail[]>();
    validationDetails.forEach(detail => {
      const existing = fileDetailsMap.get(detail.fileName) || [];
      existing.push(detail);
      fileDetailsMap.set(detail.fileName, existing);
    });
    // Update matrix data for each file
    this.matrixData.forEach(matrixFile => {
      const fileName = matrixFile.fileName;
      const shortFileName = fileName.split('/').pop() || fileName;
      
      // Find matching validation details for this file
      let fileDetails = fileDetailsMap.get(fileName) || fileDetailsMap.get(shortFileName);
      
      // Try to find by partial match if exact match fails
      if (!fileDetails) {
        for (const [key, details] of fileDetailsMap.entries()) {
          if (key.includes(shortFileName) || shortFileName.includes(key)) {
            fileDetails = details;
            break;
          }
        }
      }
      
      if (fileDetails && fileDetails.length > 0) {
        // Create a map of field -> match result
        const fieldMatchMap = new Map<string, boolean>();
        fileDetails.forEach(detail => {
          // Store the field match result
          // Field names may be like "Borrower Names_1", "Borrower Signatures_2", etc.
          fieldMatchMap.set(detail.field, detail.match);
          
          // Also store without the index suffix for broader matching
          const baseField = detail.field.replace(/_\d+$/, '');
          if (!fieldMatchMap.has(baseField)) {
            fieldMatchMap.set(baseField, detail.match);
          }
        });
        // Update attributes in the matrix
        this.matrixAttributes.forEach(attr => {
          // Try to find a matching field in the validation details
          // Match by exact name, or by base name (without index)
          let matched: boolean | undefined;
          let matchedFieldsCount = 0;
          let totalFieldsCount = 0;
          
          // First try exact match with the attribute
          if (fieldMatchMap.has(attr)) {
            matched = fieldMatchMap.get(attr);
            matchedFieldsCount++;
            totalFieldsCount++;
            } else {
            // Try to find any field that matches this attribute (base name or with index)
            for (const [fieldName, isMatch] of fieldMatchMap.entries()) {
              const baseFieldName = fieldName.replace(/_\d+$/, '');
              
              // Match if base field name equals attribute, or if field name starts with attribute_
              if (baseFieldName === attr || fieldName === `${attr}_1` || fieldName.startsWith(`${attr}_`)) {
                totalFieldsCount++;
                if (isMatch) {
                  matchedFieldsCount++;
                }
               // Aggregate: if ANY instance doesn't match, mark as N
                if (matched === undefined) {
                  matched = isMatch;
                } else {
                  matched = matched && isMatch;
                }
              }
            }
          }
          
          // Update the attribute value
          if (matched !== undefined && totalFieldsCount > 0) {
            matrixFile.attributes[attr] = matched ? 'Y' : 'N';
          } else {
            // No data for this attribute - set to unknown
            matrixFile.attributes[attr] = '-';
         }
        });
        
        // Calculate file accuracy from the MATRIX attributes (after aggregation)
        // This reflects what the user actually sees in the matrix
        const matrixAttributeValues = Object.values(matrixFile.attributes);
        const yCount = matrixAttributeValues.filter(v => v === 'Y').length;
        const nCount = matrixAttributeValues.filter(v => v === 'N').length;
        const totalEvaluated = yCount + nCount; // Exclude '-' (unknown) from accuracy calc
        
        matrixFile.accuracy = totalEvaluated > 0 
          ? parseFloat(((yCount / totalEvaluated) * 100).toFixed(1))
          : 0;
      } else {
        // No validation details found for this file
        this.matrixAttributes.forEach(attr => {
          matrixFile.attributes[attr] = '-';
        });
        matrixFile.accuracy = 0;
      }
    });
    // Recalculate attribute accuracy with actual data
    this.calculateAttributeAccuracy();
  }

  switchTab(tab: 'runs' | 'details' | 'results'): void {
    const previousTab = this.activeTab;
    this.activeTab = tab;
    // Update URL query param to persist tab state on refresh
    // Using replaceUrl: false so user can use browser back button
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tab },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });

    // If user moves away from Runs immediately after starting an evaluation,
    // resolve the currently active evaluation and show running state right away.
    if (previousTab === 'runs' && tab !== 'runs') {
      this.syncActiveEvaluationFromRunsTab();
    }

    // Service handles polling automatically through activeEvaluations$ subscription
    // No manual polling needed when switching tabs
  }

  private syncActiveEvaluationFromRunsTab(retryCount: number = 0): void {
    // Keep explicit historical selection when present.
    if (this.explicitEvaluationIdFromQuery || !this.documentCode) {
      return;
    }

    // Cache-first: reuse the runs data already loaded by the embedded EvaluationRuns component.
    // Only fall back to a fresh API call when the cache is cold (e.g. first-ever load).
    const cachedEval = this.truthManagementService.getCachedEvaluationRuns(this.documentCode, 'all');
    const cachedTest = this.truthManagementService.getCachedEvaluationRuns(this.documentCode, 'part');
    const cachedRuns = [...(cachedEval?.runs ?? []), ...(cachedTest?.runs ?? [])];
    const cachedActiveRun = cachedRuns.find(r => r.status === 'PENDING' || r.status === 'RUNNING');

    if (cachedActiveRun) {
      this.resolveActiveEvaluation(cachedActiveRun.evaluation_id);
      return;
    }

    // Cache miss — fetch fresh runs.
    this.truthManagementService.getEvaluationRuns(this.documentCode).subscribe({
      next: (response: any) => {
        const runs = Array.isArray(response?.runs) ? response.runs : [];
        const activeRun = runs.find((run: any) => run?.status === 'PENDING' || run?.status === 'RUNNING');

        if (!activeRun?.evaluation_id) {
          if (retryCount < 2) {
            setTimeout(() => this.syncActiveEvaluationFromRunsTab(retryCount + 1), 600);
          }
          return;
        }
        this.resolveActiveEvaluation(activeRun.evaluation_id);
      },
      error: () => {
        if (retryCount < 2) {
          setTimeout(() => this.syncActiveEvaluationFromRunsTab(retryCount + 1), 600);
        }
      }
    });
  }

  /** Shared helper: commit an active evaluation ID and fetch its full status once. */
  private resolveActiveEvaluation(activeEvaluationId: string): void {
    this.latestEvaluationId = activeEvaluationId;
    this.runId = activeEvaluationId;
    this.explicitEvaluationIdFromQuery = activeEvaluationId;
    this.syncEvaluationSelectionInUrl(activeEvaluationId);

    // Check service-level status store first — avoids a redundant HTTP call for
    // statuses that were already fetched by evaluation-runs.ts.
    const cachedStatus = this.truthManagementService.getLatestEvalStatus(activeEvaluationId);
    if (cachedStatus) {
      this.evaluationStatus = cachedStatus;
      this.updateProgressFromStatus(cachedStatus);
      this.updateRunSummaryFromStatus(cachedStatus);
      this.loadValidationDetailsFromAPI(activeEvaluationId, cachedStatus);
      this.cdr.detectChanges();
      return;
    }

    this.truthManagementService.getEvaluationStatus(activeEvaluationId).subscribe({
      next: (status: EvaluationStatus) => {
        this.evaluationStatus = status;
        this.updateProgressFromStatus(status);
        this.updateRunSummaryFromStatus(status);
        // Pass status so getValidationResultsByEvaluationId skips a second eval/status HTTP call
        this.loadValidationDetailsFromAPI(activeEvaluationId, status);
        this.cdr.detectChanges();
      },
      error: () => {}
    });
  }

  /**
   * Load validation details from real API using evaluation ID
   * @param overrideEvaluationId Optional evaluation ID to use instead of getEvaluationId()
   */
  loadValidationDetailsFromAPI(overrideEvaluationId?: string, cachedStatus?: EvaluationStatus): void {
   this.loadingValidationDetails = true;
    
    // Use override ID if provided, otherwise get from state
    const evaluationId = overrideEvaluationId || this.getEvaluationId();
    
    if (!evaluationId || evaluationId === 'Not Available') {
      this.loadingValidationDetails = false;
      return;
    }
   // Pass evaluationFiles to service - these contain the ground truth attributes
    this.truthManagementService.getValidationResultsByEvaluationId(evaluationId, this.evaluationFiles, cachedStatus).subscribe({
      next: (validationDetails) => {
        if (validationDetails.length > 0) {
       }
        // Filter validation details to only include fields from unique_keys_field_accuracy
        // This ensures consistency between matrix and validation details display
        const filteredByUniqueKeys = validationDetails.filter(detail => 
          this.matrixAttributes && this.matrixAttributes.includes(detail.field)
        );
        this.validationDetails = filteredByUniqueKeys;
        this.filteredDetails = filteredByUniqueKeys;
       // Group validation details by file after loading
        this.groupValidationDetailsByFile();
        
        // Update run summary with actual data
        this.updateRunSummaryFromValidationDetails(filteredByUniqueKeys);
        
        // Note: Matrix data is built from status API only (buildMatrixFromStatusResults)
        // Do NOT call updateMatrixFromValidationDetails here - it causes flicker
        
        this.loadingValidationDetails = false;
        // Trigger change detection to update UI
        this.cdr.detectChanges();
      },
      error: (error) => {
       // Clear any existing data and show empty state
        this.validationDetails = [];
        this.filteredDetails = [];
        this.fileGroups = [];
        
        this.loadingValidationDetails = false;
        // Trigger change detection to update UI
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update run summary statistics from actual validation details
   */
  updateRunSummaryFromValidationDetails(validationDetails: ValidationDetail[]): void {
    if (!validationDetails || validationDetails.length === 0) {
      return;
    } 
    // Calculate statistics from validation details
    const matchedFields = validationDetails.filter(detail => detail.match).length;
    const unmatchedFields = validationDetails.length - matchedFields;
    
    // Use status API accuracy if available, otherwise calculate from validation details
    let overallAccuracy = validationDetails.length > 0 ? (matchedFields / validationDetails.length) * 100 : 0;
    if (this.evaluationStatus?.summary?.overall_accuracy !== undefined) {
      overallAccuracy = this.evaluationStatus.summary.overall_accuracy * 100;
    }
    
    // Get unique files and attributes
    const uniqueFiles = [...new Set(validationDetails.map(detail => detail.fileName))];
    const uniqueAttributes = [...new Set(validationDetails.map(detail => detail.field))];
    
    // Update run summary
    this.runSummary = {
      ...this.runSummary,
      processedFiles: uniqueFiles.length,
      fileCount: uniqueFiles.length,
      matchedFields: matchedFields,
      unmatchedFields: unmatchedFields,
      overallAccuracy: overallAccuracy,
      uniqueAttributes: uniqueAttributes.length,
      totalAttributes: validationDetails.length
    };
    
    // Update validation run accuracy if available
    if (this.validationRun) {
      this.validationRun.accuracy = overallAccuracy;
    }
 }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  /**
   * Load matrix data from evaluation results
   */
  loadMatrixDataFromEvaluation(evaluation: any): void {
    // Don't overwrite matrix if already built from status API
    if (this.matrixBuiltFromStatusAPI) {
      return;
    }
   // Try to get matrix data from various possible sources
    let matrixData = evaluation.matrixData || evaluation.results || evaluation.files;
    
    if (!matrixData && evaluation.fileResults) {
      matrixData = evaluation.fileResults;
    }
    
    if (matrixData && Array.isArray(matrixData)) {
     // Set matrix attributes from the first file if available
      if (matrixData.length > 0 && matrixData[0].attributes) {
        this.matrixAttributes = Object.keys(matrixData[0].attributes);
     }
      
      // Transform data to match our matrix format
      this.matrixData = matrixData.map((file: any, index: number) => ({
        id: file.id || index + 1,
        fileName: file.fileName || file.file_name || `File ${index + 1}`,
        accuracy: file.accuracy || 100,
        attributes: file.attributes || {}
      }));
       // Calculate attribute accuracy
      this.calculateAttributeAccuracy();
    } else {
     // Fall back to loading from API
      this.loadDocumentKeysFromAPI();
      this.loadEvaluationFiles();
    }
  }

  /**
   * Fetch the latest evaluation status from API
   */
  fetchLatestEvaluationStatus(): void {
    // Start matrix loading
    this.loadingMatrix = true;
    
    // Use the evaluation ID from the validation run (loaded from API)
    const displayedEvalId = this.getEvaluationId();
    
    if (displayedEvalId && displayedEvalId !== 'Not Available') {
     this.latestEvaluationId = displayedEvalId;
      
      // Get the status for this specific evaluation
      this.truthManagementService.getEvaluationStatus(displayedEvalId).subscribe({
        next: (status) => {
          this.evaluationStatus = status;

          // If validationRun was not populated from local cache (cache miss path in
          // loadValidationData), build it now from the status response.
          // This eliminates the redundant loadFromAPI() call that previously made a
          // second GET /eval/status/{id} request for the same evaluation.
          if (!this.validationRun) {
            this.updateValidationRunFromStatus(status);
          }

          // Populate matrix attributes from unique_keys_field_accuracy early
          // This ensures they're available for validation details filtering in all status cases
          if (!status.unique_keys_field_accuracy) {
            this.matrixAttributes = [];
          } else {
            this.matrixAttributes = Object.keys(status.unique_keys_field_accuracy).sort();
            this.attributeAccuracy = status.unique_keys_field_accuracy;
          }

          const statusValue = status.status?.toUpperCase();
          if (statusValue === 'PENDING' || statusValue === 'RUNNING') {
            this.clearMatrixForInProgress();

            // Keep details/progress fresh while matrix remains intentionally blank.
            // matrixAttributes are now populated for validation details filtering
            this.loadValidationDetailsFromAPI(undefined, status);
            this.cdr.detectChanges();
            return;
          }
          
          // Handle FAILED status - clear matrix and stop
          if (statusValue === 'FAILED') {
            this.clearMatrixForInProgress();
            this.loadingMatrix = false;
            this.cdr.detectChanges();
            return;
          }
          
          // Initialize progress bar immediately if evaluation is running
          this.updateProgressFromStatus(status);
          
          this.updateRunSummaryFromStatus(status);
          
          // Trigger change detection to update progress bar and running status immediately
          this.cdr.detectChanges();
          
          // Build matrix data from status API results if available
          if (status.results && status.results.length > 0) {
             this.matrixBuiltFromStatusAPI = true; // Set flag to prevent overwriting
            this.buildMatrixFromStatusResults(status);
          } else {
            // Fall back to loading matrix from evaluation files API
            this.loadMatrixForEvaluation(displayedEvalId);
          }
          
          // Load validation details for the Details tab — pass status to avoid extra HTTP call
          this.loadValidationDetailsFromAPI(undefined, status);
        },
        error: (error) => {
         // Fall back to getting latest from API
          this.fetchLatestFromAPI();
        }
      });
    } else {
    this.loadingMatrix = false;
      this.fetchLatestFromAPI();
    }
  }
  
  /**
   * Fallback method to fetch latest evaluation from API
   */
  fetchLatestFromAPI(): void {
    // Start loading state for matrix
    this.loadingMatrix = true;

    const explicitEvaluationId = this.getEffectiveEvaluationId();
    if (explicitEvaluationId) {
      // Do not override explicit historical selection with latest active run.
      this.truthManagementService.getEvaluationStatus(explicitEvaluationId).subscribe({
        next: (status) => {
          this.evaluationStatus = status;

          const statusValue = status.status?.toUpperCase();
          if (statusValue === 'PENDING' || statusValue === 'RUNNING') {
           this.clearMatrixForInProgress();
            this.loadValidationDetailsFromAPI(explicitEvaluationId);
            this.cdr.detectChanges();
            return;
          }
          
          // Handle FAILED status - clear matrix and stop
          if (statusValue === 'FAILED') {
            this.clearMatrixForInProgress();
            this.loadingMatrix = false;
            this.cdr.detectChanges();
            return;
          }
          
          // Initialize progress bar immediately if evaluation is running
          this.updateProgressFromStatus(status);
          
          this.updateRunSummaryFromStatus(status);
          
          // Trigger change detection to update progress bar and running status immediately
          this.cdr.detectChanges();

          if (status.results && status.results.length > 0) {
            this.matrixBuiltFromStatusAPI = true;
            this.buildMatrixFromStatusResults(status);
          } else {
            this.loadMatrixForEvaluation(explicitEvaluationId);
          }
        },
        error: (error) => {
          this.loadingMatrix = false;
        }
      });
      return;
    }
    
    // Get the latest evaluation ID from API as fallback
    this.truthManagementService.getLatestEvaluationId(this.documentCode).subscribe({
      next: (evaluationId) => {
       this.latestEvaluationId = evaluationId;
        
        // Then get the status for this evaluation
        if (evaluationId && evaluationId !== 'Not Available') {
          this.truthManagementService.getEvaluationStatus(evaluationId).subscribe({
            next: (status) => {
              this.evaluationStatus = status;

              const statusValue = status.status?.toUpperCase();
              if (statusValue === 'PENDING' || statusValue === 'RUNNING') {
                this.clearMatrixForInProgress();
                this.loadValidationDetailsFromAPI(evaluationId);
                this.cdr.detectChanges();
                return;
              }
              
              // Handle FAILED status - clear matrix and stop
              if (statusValue === 'FAILED') {
                this.clearMatrixForInProgress();
                this.loadingMatrix = false;
                this.cdr.detectChanges();
                return;
              }
              
              // Initialize progress bar immediately if evaluation is running
              this.updateProgressFromStatus(status);
              
              this.updateRunSummaryFromStatus(status);
              
              // Trigger change detection to update progress bar and running status immediately
              this.cdr.detectChanges();
              
              // Build matrix data from status API results if available
              if (status.results && status.results.length > 0) {
              this.matrixBuiltFromStatusAPI = true; // Set flag to prevent overwriting
                this.buildMatrixFromStatusResults(status);
              } else {
                // Fall back to loading matrix from evaluation files API
                this.loadMatrixForEvaluation(evaluationId);
              }
            },
            error: (error) => {
              this.loadingMatrix = false;
              // Fall back to API-based data loading
              this.updateRunSummaryFromActualData();
              this.loadDocumentKeysFromAPI();
              this.loadEvaluationFiles();
            }
          });
        } else {
          this.loadingMatrix = false;
          this.updateRunSummaryFromActualData();
          this.loadDocumentKeysFromAPI();
          this.loadEvaluationFiles();
        }
      },
      error: (error) => {
        this.loadingMatrix = false;
        // Fall back to API-based data loading
        this.updateRunSummaryFromActualData();
        this.loadDocumentKeysFromAPI();
        this.loadEvaluationFiles();
      }
    });
  }
  
  /**
   * Build matrix data directly from status API results
   * This method populates the validation matrix using the field_results from the status API
   */
  buildMatrixFromStatusResults(status: EvaluationStatus): void {
    if (!status.results || status.results.length === 0) {
      this.loadingMatrix = false;
      return;
    }
    
    // Extract field names and accuracy from API (only available when COMPLETED/FAILED)
    if (!status.unique_keys_field_accuracy) {
      this.loadingMatrix = false;
      return;
    }
    
    this.matrixAttributes = Object.keys(status.unique_keys_field_accuracy).sort();
    // Track authoritative field names from API - these should NOT be formatted
    this.authoritativeFieldNames = new Set(this.matrixAttributes);
    this.attributeAccuracy = status.unique_keys_field_accuracy;
    // Build matrix data for each SUCCESSFUL file
    this.matrixData = status.results.map(fileResult => {
      const attributes: { [key: string]: string } = {};
      
      // For each attribute, determine if it's Y (matched), N (not matched), or blank (not present)
      this.matrixAttributes.forEach(attr => {
        const fieldResult = fileResult.field_results?.[attr];
        if (fieldResult !== undefined) {
          // Handle new API format where fact can be an array
          let factArray = Array.isArray(fieldResult.fact) ? fieldResult.fact : [fieldResult.fact];
          
          // Filter out empty strings and N/A from fact array
          const factArrayFiltered = factArray.filter((val: any) => 
            val && (val.trim ? val.trim() !== '' : val !== '') && val !== 'N/A' && val !== 'NA' && val !== 'n/a'
          );
          
          // Skip fields where ground truth is empty or all N/A
          if (factArrayFiltered.length === 0) {
            attributes[attr] = ''; // Treat as blank - exclude from accuracy
          } else if (fieldResult.matched) {
            attributes[attr] = 'Y';
          } else {
            attributes[attr] = 'N';
          }
        } else {
          // Field not present for this file - leave blank (empty string)
          attributes[attr] = '';
        }
      });
      
      // Use file-level accuracy from API
      const accuracy = fileResult.file_accuracy;
      
      return {
        fileName: fileResult.file_name,
        accuracy: accuracy,
        attributes: attributes,
        isFailed: false
      };
    });
    
    // Add FAILED files to matrix with empty cells
    if (status.files && status.files.length > 0) {
      const failedFiles = status.files.filter(file => file.status === 'FAILED');
      
      if (failedFiles.length > 0) { 
        failedFiles.forEach(failedFile => {
          const attributes: { [key: string]: string } = {};
          
          // All attributes are empty for failed files
          this.matrixAttributes.forEach(attr => {
            attributes[attr] = '';
          });
          
          this.matrixData.push({
            fileName: failedFile.file_name,
            accuracy: null,
            attributes: attributes,
            isFailed: true
          });
        });
      }
    }
    
    // Filter out files with ALL empty attributes (no ground truth to validate)
    // Keep FAILED files - they represent processing failures, not empty ground truth
    this.matrixData = this.matrixData.filter(file => {
      if (file.isFailed) {
        return true; // Keep failed files to show processing errors
      }
      // Check if ANY attribute has a value (Y, N, or non-empty)
      return Object.values(file.attributes).some((val: any) => val && typeof val === 'string' && val.trim() !== '');
    });
    
    // Calculate overall accuracy from status summary or compute from matrix
    if (status.summary?.overall_accuracy !== undefined) {
      this.overallAccuracy = status.summary.overall_accuracy * 100;
    } else {
      const accuracyValues = Object.values(this.attributeAccuracy) as number[];
      const totalMatch = accuracyValues.reduce((sum, acc) => sum + acc, 0);
      const displayedFieldsCount = this.matrixAttributes.filter(attr => this.shouldDisplayField(attr)).length;
      this.overallAccuracy = displayedFieldsCount > 0 
        ? totalMatch / displayedFieldsCount 
        : 0;
    }
    // Matrix loading complete
    this.loadingMatrix = false;
    this.cdr.detectChanges();
  }
  
  /**
   * Load matrix data for a specific evaluation ID
   */
  loadMatrixForEvaluation(evaluationId: string): void {
    // Skip if matrix already built from status API (prevents flicker)
    if (this.matrixBuiltFromStatusAPI) {
       ('⏭️ Skipping loadMatrixForEvaluation - matrix already built from status API');
      return;
    }

    // Never load fallback matrix rows while an evaluation is still running.
    if (this.isEvaluationRunning()) {
      this.clearMatrixForInProgress();
      return;
    }
    // Load document keys for matrix headers
    if (this.documentCode) {
      this.loadDocumentKeysFromAPI();
    }
    
    // Load evaluation files for this specific evaluation
    this.loadingEvalFiles = true;
    this.truthManagementService.getEvaluationFiles(this.documentCode).subscribe({
      next: (evaluationFiles: EvaluationFile[]) => {
        // RE-CHECK flag inside callback - status API may have completed while waiting
        if (this.matrixBuiltFromStatusAPI) {
           ('⏭️ Skipping loadMatrixForEvaluation callback - matrix was built while waiting');
          this.loadingEvalFiles = false;
          return;
        }
        if (evaluationFiles && evaluationFiles.length > 0) {
          // Filter files for this specific evaluation if possible
          let filteredFiles = evaluationFiles;
          
          // If we can identify files for this evaluation, filter them
          if (evaluationFiles.some((f: any) => f.evaluation_id)) {
            filteredFiles = evaluationFiles.filter((f: any) => f.evaluation_id === evaluationId);
          }
          
          this.evaluationFiles = filteredFiles;
          this.updateMatrixWithEvaluationFiles(filteredFiles);
        } else {
        }
        
        this.loadingEvalFiles = false;
      },
      error: (error) => {
        this.loadingEvalFiles = false;
      }
    });
  }
  
  /**
   * Update run summary from evaluation status API response
   */
  updateRunSummaryFromStatus(status: EvaluationStatus): void {
    const totalFields = status.summary?.total_fields || 0;
    const matchedFields = status.summary?.total_matched_fields || 0;
    
    this.runSummary = {
      totalFiles: status.total_files || 0,
      processedFiles: status.completed_files || 0,
      fileCount: status.total_files || 0,
      matchedFields,
      unmatchedFields: Math.max(totalFields - matchedFields, 0),
      overallAccuracy: this.calculateAccuracyFromStatus(status),
      uniqueAttributes: 0,
      totalAttributes: totalFields
    };
  }
  
  /**
   * Calculate accuracy percentage from evaluation status
   * Uses the status API's accuracy fields which are the authoritative source
   */
  calculateAccuracyFromStatus(status: EvaluationStatus): number {
    // Use summary.overall_accuracy (0-1 scale, multiply by 100)
    if (status.summary?.overall_accuracy !== undefined) {
      return status.summary.overall_accuracy * 100;
    }
    // Fallback: Calculate from matched fields if available
    if (status.summary?.total_fields && status.summary?.total_matched_fields !== undefined) {
      const accuracy = (status.summary.total_matched_fields / status.summary.total_fields) * 100;
      return accuracy;
      return accuracy;
    }
    // Fallback: Return 0 if no accuracy data available
   return 0;
  }

  /**
   * Check if the current evaluation is running (PENDING or RUNNING)
   * Checks both evaluationStatus (from polling) and validationRun (from initial load)
   */
  isEvaluationRunning(): boolean {
    // First check evaluationStatus if available (from status polling API)
    if (this.evaluationStatus) {
      const status = this.evaluationStatus.status?.toUpperCase();
      return status === 'PENDING' || status === 'RUNNING';
    }
    
    // Fallback to validationRun status (from initial load)
    if (this.validationRun) {
      const status = this.validationRun.status?.toUpperCase();
      return status === 'PENDING' || status === 'RUNNING' || status === 'IN PROGRESS';
    }
    
    return false;
  }

  /**
   * Keep matrix intentionally blank while evaluation is in progress.
   */
  private clearMatrixForInProgress(): void {
    this.matrixBuiltFromStatusAPI = false;
    this.matrixData = [];
    this.matrixAttributes = [];
    this.authoritativeFieldNames.clear();
    this.attributeAccuracy = {};
    this.overallAccuracy = 0;
    this.loadingMatrix = false;
  }

  /**
   * Get evaluation progress percentage (smoothly animated)
   */
  getEvaluationProgress(): number {
    // Return the smoothly animated progress for display
    return this.getAnimatedProgress();
  }
  
  /**
   * Get raw progress percentage (for internal use)
   */
  getRawProgress(): number {
    if (!this.evaluationStatus) return 0;
    const total = this.evaluationStatus.total_files || 0;
    const completed = this.evaluationStatus.completed_files || 0;
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  shouldDisplayField(fieldName: string): boolean {
    return true;
  }

  /**
   * Format field name for consistent array numbering (_1, _2, _3)
   * Uses API metadata (is_array flag) to determine if field should have _1 suffix
   * @param fieldName - Raw field name from API (e.g., "Borrower Names", "Property Addresses_2", etc.)
   * @returns Formatted field name (e.g., "Borrower Names_1", "Property Addresses_2")
   */
  formatFieldName(fieldName: string): string {
    // PRIORITY: If field is from unique_keys_field_accuracy (authoritative source), return as-is
    // These field names come directly from the API and should never be modified
    if (this.authoritativeFieldNames.has(fieldName)) {
      return fieldName;
    }

    // Already numbered? (e.g., "_2", "_3", etc.) → Keep it as-is
    if (/_\d+$/.test(fieldName)) {
      return fieldName;
    }

    // Check if BOTH base name AND base_1 exist in matrixAttributes
    // If they do, they are truly separate unique keys from API - don't rename
    const baseWithOne = `${fieldName}_1`;
    if (this.matrixAttributes?.includes(fieldName) && this.matrixAttributes?.includes(baseWithOne)) {
      // Both exist as separate unique_keys - keep original name to avoid duplicates
      return fieldName;
    }
    
    // Check if this exact field has numbered siblings (_2, _3, etc.)
    const hasNumberedSiblings = 
      this.matrixAttributes?.some(attr => 
        attr.startsWith(`${fieldName}_`) && /_\d+$/.test(attr)
      ) ||
      this.validationDetails?.some(d => 
        d.field.startsWith(`${fieldName}_`) && /_\d+$/.test(d.field)
      );
    
    if (hasNumberedSiblings) {
      return `${fieldName}_1`;
    }
    
    // Use API metadata to determine if field is an array
    // Try both the original field name and normalized versions (handle spaces/underscores)
    let metadata = this.fieldMetadata.get(fieldName);
    if (!metadata) {
      // Try with spaces replaced by underscores
      const normalizedName = fieldName.replace(/ /g, '_');
      metadata = this.fieldMetadata.get(normalizedName);
    }
    if (!metadata) {
      // Try with underscores replaced by spaces
      const normalizedName = fieldName.replace(/_/g, ' ');
      metadata = this.fieldMetadata.get(normalizedName);
    }
    
    if (metadata && metadata.isArray) {
      return `${fieldName}_1`;
    }
    
    // Not an array field, return as-is
    return fieldName;
  }

  private getFieldMetadataForMatrixAttr(fieldName: string): { isArray: boolean; isTable: boolean } | null {
    if (!fieldName) {
      return null;
    }

    const baseName = fieldName.replace(/_\d+$/, '');
    const candidates = [
      fieldName,
      baseName,
      fieldName.replace(/ /g, '_'),
      fieldName.replace(/_/g, ' '),
      baseName.replace(/ /g, '_'),
      baseName.replace(/_/g, ' ')
    ];

    for (const candidate of candidates) {
      const metadata = this.fieldMetadata.get(candidate);
      if (metadata) {
        return metadata;
      }
    }

    return null;
  }

  isMatrixFieldArray(fieldName: string): boolean {
    const metadata = this.getFieldMetadataForMatrixAttr(fieldName);
    if (metadata) {
      // Table fields are JSON-schema arrays internally but render as a single
      // table column, so they should never show the GROUP indicator.
      return !!metadata.isArray && !metadata.isTable;
    }

    return /_\d+$/.test(fieldName || '');
  }

  getMatrixFieldTypeIndicator(fieldName: string): string {
    return this.isMatrixFieldArray(fieldName) ? 'GROUP' : 'NRM';
  }

  getMatrixFieldTypeLabel(fieldName: string): string {
    return this.isMatrixFieldArray(fieldName) ? 'Multiple Values' : 'Normal field';
  }

  /**
   * Get evaluation ID for display
   */
  getEvaluationId(): string {
    // When runId is "latest", use the validationRun which is populated from API
    if (this.runId === 'latest' || this.runId === 'Not Available') {
      // If we have a validation run with evaluation ID (loaded from API), use that
      if (this.validationRun) {
        const evalId = (this.validationRun as any)?.evaluationId;
        if (evalId && evalId !== 'latest' && evalId !== 'Not Available') {
          return evalId;
        }
        
        if (this.validationRun.runId && this.validationRun.runId !== 'latest') {
          return this.validationRun.runId;
        }
      }
      
      // Use the latest evaluation ID from API
      if (this.latestEvaluationId && this.latestEvaluationId !== 'Not Available') {
        return this.latestEvaluationId;
      }
      
      return 'Not Available';
    }
    
    // If we have a validation run with evaluation ID, use that
    if (this.validationRun) {
      // Try to get the actual evaluation ID first
      const evalId = (this.validationRun as any)?.evaluationId;
      if (evalId && evalId !== 'latest') {
        return evalId;
      }
      
      // Fall back to runId if it looks like a real ID
      if (this.validationRun.runId && this.validationRun.runId !== 'latest') {
        return this.validationRun.runId;
      }
    }
    
    // Use the latest evaluation ID from API as last resort
    if (this.latestEvaluationId && this.latestEvaluationId !== 'Not Available') {
      return this.latestEvaluationId;
    }
    
    // If we have a runId parameter that looks real, use it
    if (this.runId && this.runId !== 'latest') {
      return this.runId;
    }
    
    return 'Not Available';
  }

  truncateDocumentModelName(name: string | null | undefined, maxLength: number = 24): string {
    if (!name) {
      return '-';
    }
    return name.length > maxLength ? `${name.slice(0, maxLength - 1)}…` : name;
  }

  formatDocumentModelForDisplay(name: string | null | undefined): string {
    if (!name) {
      return '-';
    }

    return name
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(part => part.length > 0)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private getSummaryTotalFields(): number {
    return this.evaluationStatus?.summary?.total_fields ?? this.runSummary.totalAttributes ?? 0;
  }

  getSummaryMatchedFields(): number {
    if (this.evaluationStatus?.summary?.total_matched_fields !== undefined) {
      return this.evaluationStatus.summary.total_matched_fields;
    }
    return this.runSummary.matchedFields || 0;
  }

  getSummaryUnmatchedFields(): number {
    const total = this.getSummaryTotalFields();
    if (total > 0) {
      return Math.max(total - this.getSummaryMatchedFields(), 0);
    }
    return this.runSummary.unmatchedFields || 0;
  }

  getSummaryMatchedPercent(): number {
    const total = this.getSummaryTotalFields();
    if (!total) {
      return 0;
    }
    return Math.round((this.getSummaryMatchedFields() / total) * 100);
  }

  getSummaryUnmatchedPercent(): number {
    const total = this.getSummaryTotalFields();
    if (!total) {
      return 0;
    }
    return Math.round((this.getSummaryUnmatchedFields() / total) * 100);
  }

  // ========== Pie Chart Helper Methods ==========
  
  /**
   * Calculate stroke-dasharray for file status pie chart segments
   */
  getFileStatusDashArray(type: 'completed' | 'running' | 'failed' | 'pending'): string {
    const total = this.evaluationStatus?.total_files || this.runSummary?.fileCount || 0;
    if (total === 0) return '0 100';
    
    let count = 0;
    switch (type) {
      case 'completed':
        count = this.evaluationStatus?.completed_files || this.runSummary?.processedFiles || 0;
        break;
      case 'running':
        count = this.evaluationStatus?.running_files || 0;
        break;
      case 'failed':
        count = this.evaluationStatus?.failed_files || 0;
        break;
      case 'pending':
        count = this.evaluationStatus?.pending_files || 0;
        break;
    }
    
    const percentage = (count / total) * 100;
    return `${percentage} 100`;
  }
  
  /**
   * Calculate stroke-dashoffset for file status pie chart segments
   */
  getFileStatusOffset(type: 'running' | 'failed' | 'pending'): number {
    const total = this.evaluationStatus?.total_files || this.runSummary?.fileCount || 0;
    if (total === 0) return 0;
    
    const completed = ((this.evaluationStatus?.completed_files || this.runSummary?.processedFiles || 0) / total) * 100;
    const running = ((this.evaluationStatus?.running_files || 0) / total) * 100;
    const failed = ((this.evaluationStatus?.failed_files || 0) / total) * 100;
    
    switch (type) {
      case 'running':
        return completed;
      case 'failed':
        return completed + running;
      case 'pending':
        return completed + running + failed;
    }
  }

  /**
   * Start editing the evaluation ID (inline GUID input)
   */
  startEvaluationEdit(): void {
    this.editingEvaluationId = true;
    this.evaluationIdInput = this.getEvaluationId();
  }

  /**
   * Cancel editing the evaluation ID
   */
  cancelEvaluationEdit(): void {
    this.editingEvaluationId = false;
    this.evaluationIdInput = '';
  }

  /**
   * Handle blur event on evaluation ID input
   */
  onEvaluationInputBlur(): void {
    // Auto-cancel on blur if no valid input
    if (!this.evaluationIdInput || this.evaluationIdInput.length < 36) {
      setTimeout(() => {
        if (this.editingEvaluationId) {
          this.cancelEvaluationEdit();
        }
      }, 200);
    }
  }

  /**
   * Block non-GUID characters on keypress
   */
  onKeyPress(event: KeyboardEvent): boolean {
    const char = event.key;
    const allowedChars = /[0-9a-fA-F\-]/;
    
    if (!allowedChars.test(char) && char !== 'Backspace' && char !== 'Delete') {
      event.preventDefault();
      return false;
    }
    return true;
  }

  /**
   * Filter pasted content to only allow GUID characters
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const filtered = pastedText.replace(/[^0-9a-fA-F\-]/g, '');
    this.evaluationIdInput = filtered;
  }

  /**
   * Validate GUID format on model change
   */
  onEvaluationIdChange(value: string): void {
    this.evaluationIdInput = value.replace(/[^0-9a-fA-F\-]/g, '');
    // Clear error when user starts typing again
    if (this.evaluationError) {
      this.evaluationError = null;
    }
  }

  /**
   * Check if the current evaluation ID input is a valid GUID format
   */
  isValidEvaluationId(): boolean {
    if (!this.evaluationIdInput || this.evaluationIdInput.trim() === '') {
      return false;
    }
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(this.evaluationIdInput);
  }

  /**
   * Load evaluation data by the entered ID
   */
  loadEvaluationById(): void {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (!guidRegex.test(this.evaluationIdInput)) {
      this.evaluationError = 'Please enter a valid evaluation ID (GUID format)';
      return;
    }

    const newEvaluationId = this.evaluationIdInput;
    // Clear any previous error
    this.evaluationError = null;
    
    // Reset matrix built flag to allow fresh data
    this.matrixBuiltFromStatusAPI = false;
    
    // Load fresh evaluation status and results - DON'T close edit mode until we know the ID is valid
    this.truthManagementService.getEvaluationStatus(newEvaluationId).subscribe({
      next: (status: EvaluationStatus) => {
        // Only update state AFTER successful API response
        this.explicitEvaluationIdFromQuery = newEvaluationId;
        this.latestEvaluationId = newEvaluationId;
        this.runId = newEvaluationId;
        this.syncEvaluationSelectionInUrl(newEvaluationId);
        
        // Update validationRun with new evaluation ID
        if (this.validationRun) {
          this.validationRun = {
            ...this.validationRun,
            runId: newEvaluationId,
            evaluationId: newEvaluationId
          };
        }
        
        // Close edit mode only after successful load
        this.editingEvaluationId = false;
        
        this.evaluationStatus = status;
        this.evaluationError = null;
        
        // Update validation run from status
        this.updateValidationRunFromStatus(status);
        
        // Build matrix from results
        if (status.results && status.results.length > 0) {
          this.buildMatrixFromStatusResults(status);
        } else {
          this.loadMatrixForEvaluation(newEvaluationId);
        }
        
        // Load validation details - pass ID explicitly to avoid stale state
        this.loadValidationDetailsFromAPI(newEvaluationId);
        
        // Update run summary
        this.updateRunSummaryFromStatus(status);
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        // Keep edit mode open and show error - don't navigate away
        this.evaluationError = `Evaluation ID "${newEvaluationId}" not found. Please check the ID and try again.`;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Receive latest runs data emitted by the embedded EvaluationRuns component.
   * This avoids a duplicate getEvaluationRuns() API call in the parent.
   * Only acts when the parent has no explicit selection and no validationRun yet.
   */
  onLatestRunsLoaded(runs: { evalRun: ValidationRun | null; testRun: ValidationRun | null }): void {
    clearTimeout(this.latestRunsLoadTimeout);
    this.latestRunsDataReceived = true;

    // If the parent already has an explicit selection or a validationRun, do nothing.
    if (this.validationRun || this.explicitEvaluationIdFromQuery) {
      return;
    }

    const latestRun = runs.evalRun ?? runs.testRun;
    if (!latestRun?.evaluationId) {
      // No runs available — show placeholder.
      this.createPlaceholderRun();
      return;
    }

    // Populate the parent's validationRun from the data we already have.
    // No extra API call needed for basic run metadata.
    this.validationRun = latestRun;
    this.runId = latestRun.evaluationId!;
    this.latestEvaluationId = latestRun.evaluationId!;

    // Load document attribute keys (needed for the results matrix column headers).
    if (this.documentCode && !this.loadingKeys) {
      this.loadDocumentKeysFromAPI();
    }

    // Fetch full eval/status once for progress bar, summary, and details.
    // evaluationStatusChanged from EvaluationRuns will also arrive, but
    // fetchLatestEvaluationStatus() de-dupes via the existing shareReplay cache.
    this.fetchLatestEvaluationStatus();
    this.cdr.detectChanges();
  }

  /**
   * Handle evaluation selection from embedded runs component
   */
  onEvaluationSelected(evaluationId: string): void {
    // Update the evaluation ID for display
    this.latestEvaluationId = evaluationId;
    this.runId = evaluationId;
    
    // Reset matrix built flag to allow fresh data
    this.matrixBuiltFromStatusAPI = false;
    
    // Lock to the selected evaluation ID for both running and completed runs.
    this.explicitEvaluationIdFromQuery = evaluationId;

    // Switch to details tab
    this.activeTab = 'details';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'details', evaluationId: evaluationId },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });
    
    // Load fresh evaluation status first to determine if it's active or completed
    this.truthManagementService.getEvaluationStatus(evaluationId).subscribe({
      next: (freshStatus: EvaluationStatus) => {
        this.evaluationStatus = freshStatus;
        
        // Update validation run with new data (this updates the Run Summary display)
        this.updateValidationRunFromStatus(freshStatus);
        
        // Build matrix from results
        if (freshStatus.results && freshStatus.results.length > 0) {
          this.buildMatrixFromStatusResults(freshStatus);
        } else {
          // Load matrix for this evaluation if no results in status
          this.loadMatrixForEvaluation(evaluationId);
        }
        
        // Load validation details from API — pass freshStatus to avoid extra eval/status HTTP call
        this.loadValidationDetailsFromAPI(evaluationId, freshStatus);
        
        // Update run summary
        this.updateRunSummaryFromStatus(freshStatus);
        this.cdr.detectChanges();
      },
      error: (error) => {
        // Fallback to basic loading - pass ID explicitly
        this.loadMatrixForEvaluation(evaluationId);
        this.loadValidationDetailsFromAPI(evaluationId);
      }
    });
  }

  /**
   * Handle evaluation status changes from embedded runs component
   */
  onEvaluationStatusChanged(status: EvaluationStatus | null): void {
    // When a specific past run is selected, ignore unrelated live status updates
    // except while user is on Runs tab (where a new run can be started).
    const selectedEvaluationId = this.explicitEvaluationIdFromQuery;
    if (this.activeTab !== 'runs' && selectedEvaluationId && status?.evaluation_id && status.evaluation_id !== selectedEvaluationId) {
      return;
    }
    
    const previousStatus = this.evaluationStatus?.status;
    const previousEvaluationId = this.evaluationStatus?.evaluation_id || this.latestEvaluationId;
    this.evaluationStatus = status;

    if (status && (status.status === 'PENDING' || status.status === 'RUNNING')) {
      this.clearMatrixForInProgress();
    }
    
    // Check if the evaluation ID has changed (user manually edited it)
    if (status && status.evaluation_id && status.evaluation_id !== previousEvaluationId) {
      // On Runs tab, treat incoming status as the active selection so Details/Results
      // immediately follows the newly started evaluation.
      if (this.activeTab === 'runs' && (status.status === 'PENDING' || status.status === 'RUNNING')) {
        this.explicitEvaluationIdFromQuery = status.evaluation_id;
        this.syncEvaluationSelectionInUrl(status.evaluation_id);
      }
      
      // Update the latest evaluation ID
      this.latestEvaluationId = status.evaluation_id;
      
      // Update the validationRun with the new evaluation ID
      if (this.validationRun) {
        this.validationRun = {
          ...this.validationRun,
          runId: status.evaluation_id,
          evaluationId: status.evaluation_id
        };
      }
      
      // For completed evaluations, use loadCompletedEvaluationResults which handles everything
      if (status.status === 'COMPLETED' || status.status === 'FAILED') {
        this.loadCompletedEvaluationResults(status.evaluation_id);
        return; // loadCompletedEvaluationResults handles everything
      }
      
      // For in-progress evaluations, reload the validation details - pass ID explicitly
      this.loadValidationDetailsFromAPI(status.evaluation_id);

      // Keep matrix blank during in-progress states.
      this.clearMatrixForInProgress();
    }
    
    // Service handles polling automatically - no manual restart needed
    
    // Update smooth progress animation
    if (status) {
      this.updateProgressFromStatus(status);
    }
    
    // Check if evaluation just completed (same evaluation, status changed to completed)
    if (status && (status.status === 'COMPLETED' || status.status === 'FAILED') && 
        previousStatus !== 'COMPLETED' && previousStatus !== 'FAILED') {
      // Store the evaluation ID for loading
      const completedEvaluationId = status.evaluation_id;
      if (completedEvaluationId) {
        this.latestEvaluationId = completedEvaluationId;

        // Deduplication: evaluationCompleted$ (parent-level) may already be handling this.
        // Mark as handled here so the completionSubscription skips it.
        if (this.recentlyHandledCompletions.has(completedEvaluationId)) { return; }
        this.recentlyHandledCompletions.add(completedEvaluationId);
        setTimeout(() => this.recentlyHandledCompletions.delete(completedEvaluationId), 5000);

        // Auto-load the results after a short delay to ensure API has updated
        setTimeout(() => {
          this.loadCompletedEvaluationResults(completedEvaluationId);
        }, 200);
      }
    }
  }
  
  /**
   * Sync displayed progress with backend status.
   * First update for an evaluation snaps immediately; later updates animate smoothly.
   */
  private updateProgressFromStatus(status: EvaluationStatus): void {
    const total = status.total_files || 0;
    const completed = status.completed_files || 0;
    const nextProgress = total > 0 ? Math.round((completed / total) * 100) : 0;

    const evaluationId = status.evaluation_id || this.getEvaluationId();
    const firstUpdateForEvaluation = this.progressInitializedForEvaluationId !== evaluationId;

    this.targetProgress = nextProgress;

    if (firstUpdateForEvaluation) {
      if (this.progressAnimationFrame) {
        cancelAnimationFrame(this.progressAnimationFrame);
      }
      this.displayedProgress = nextProgress;
      this.progressInitializedForEvaluationId = evaluationId;
      this.cdr.detectChanges();
      return;
    }

    this.animateProgress();
  }

  /**
   * Animate progress bar smoothly
   */
  private animateProgress(): void {
    if (this.progressAnimationFrame) {
      cancelAnimationFrame(this.progressAnimationFrame);
    }
    
    const animate = () => {
      const diff = this.targetProgress - this.displayedProgress;
      if (Math.abs(diff) < 0.5) {
        this.displayedProgress = this.targetProgress;
        this.cdr.detectChanges(); // Trigger change detection
        return;
      }
      
      // Smooth easing - move 10% of the remaining distance each frame
      this.displayedProgress += diff * 0.1;
      this.cdr.detectChanges(); // Trigger change detection
      this.progressAnimationFrame = requestAnimationFrame(animate);
    };
    
    // Run animation inside Angular zone
    this.ngZone.run(() => animate());
  }
  
  /**
   * Get smoothly animated progress for display
   */
  getAnimatedProgress(): number {
    return Math.round(this.displayedProgress);
  }
  
  /**
   * Load results for a completed evaluation
   */
  private loadCompletedEvaluationResults(evaluationId: string): void {
    // Update the run ID to the completed evaluation
    this.runId = evaluationId;
    
    // Also update the latestEvaluationId to ensure getEvaluationId() returns the correct value
    this.latestEvaluationId = evaluationId;
    
    // Reset matrix built flag to allow fresh data
    this.matrixBuiltFromStatusAPI = false;
    
    // Fetch fresh evaluation status with results
    this.truthManagementService.getEvaluationStatus(evaluationId).subscribe({
      next: (freshStatus: EvaluationStatus) => {
        this.evaluationStatus = freshStatus;
        
        // Update validation run with new data
        this.updateValidationRunFromStatus(freshStatus);
        
        // Build matrix from fresh results
        if (freshStatus.results && freshStatus.results.length > 0) {
          this.buildMatrixFromStatusResults(freshStatus);
        }
        
        // Load validation details from API — pass freshStatus to avoid extra eval/status HTTP call
        this.loadValidationDetailsFromAPI(evaluationId, freshStatus);
        
        // Update run summary
        this.updateRunSummaryFromStatus(freshStatus);
      },
      error: (error) => {
      }
    });
  }
  
  /**
   * Update validation run object from fresh status
   */
  private updateValidationRunFromStatus(status: EvaluationStatus): void {
    let accuracy = 0;
    if (status.summary?.overall_accuracy !== undefined) {
      accuracy = status.summary.overall_accuracy * 100;
    }
    
    // Calculate duration from status
    const duration = this.calculateDuration(
      (status as any).time_taken_seconds,
      status.started_at,
      status.completed_at
    );
    
    // Parse date with proper UTC handling
    let createdDate: Date;
    if (status.started_at) {
      const dateStr = status.started_at.endsWith('Z') ? status.started_at : status.started_at + 'Z';
      createdDate = new Date(dateStr);
    } else {
      createdDate = new Date();
    }
    
    this.validationRun = {
      runId: status.evaluation_id,
      evaluationId: status.evaluation_id,
      loanId: this.loanId,
      loanNumber: status.document_model || this.documentCode,
      documentCode: status.document_model || this.documentCode,
      accuracy: accuracy,
      status: status.status === 'COMPLETED' ? 'Completed' : status.status,
      createdDate: createdDate,
      duration: duration,
      totalFiles: status.total_files,
      completedFiles: status.completed_files,
      failedFiles: status.failed_files,
      ocrMode: status.ocr_mode || undefined,
      llmModel: status.llm_model || undefined
    };

    this.overallAccuracy = accuracy;
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data-extraction']);
  }

  navigateToExtractionEval(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  /**
   * Extract just the filename from a full path
   */
  getShortFileName(fullPath: string): string {
    if (!fullPath) return '';
    // Handle both forward and backslashes
    const parts = fullPath.split(/[\/\\]/);
    return parts[parts.length - 1] || fullPath;
  }

  /**
   * Remove ground_truths/ prefix from filename for display in matrix
   */
  getCleanFileName(fullPath: string): string {
    if (!fullPath) return '';
    // Extract just the filename from the full path (removes all folder paths)
    const parts = fullPath.split(/[\/\\]/);
    return parts[parts.length - 1] || fullPath;
}

  private normalizeFieldKey(fieldName: string): string {
    return (fieldName || '').trim().toLowerCase().replace(/\s+/g, '_');
  }

  private normalizeFileKey(fileName: string): string {
    return this.getCleanFileName(fileName || '').trim().toLowerCase();
  }

  private getFailedFileForAttribute(attribute: string): { fileName: string } | null {
    const targetField = this.normalizeFieldKey(attribute);

    // Primary source: validation details mismatch rows (independent from rendered matrix ordering).
    const failedDetail = this.validationDetails.find(detail => {
      return !detail.match && this.normalizeFieldKey(detail.field) === targetField;
    });
    if (failedDetail?.fileName) {
      return { fileName: failedDetail.fileName };
    }

    // Fallback source: matrix cells marked as mismatch.
    const failedMatrixRow = this.matrixData.find(file => file?.attributes?.[attribute] === 'N');
    if (failedMatrixRow?.fileName) {
      return { fileName: failedMatrixRow.fileName };
    }

    return null;
  }

  private resolveEvaluationFileUuid(fileName: string): string | null {
    if (!fileName || this.evaluationFiles.length === 0) {
      return null;
    }

    const normalizedTarget = this.normalizeFileKey(fileName);
    const exact = this.evaluationFiles.find(file => this.normalizeFileKey(file.file_name) === normalizedTarget);
    if (exact?.uuid) {
      return exact.uuid;
    }

    const fuzzy = this.evaluationFiles.find(file => this.normalizeFileKey(file.file_name).includes(normalizedTarget) || normalizedTarget.includes(this.normalizeFileKey(file.file_name)));
    return fuzzy?.uuid || null;
  }

  isAttributeActionEnabled(attribute: string): boolean {
    const value = Number(this.attributeAccuracy?.[attribute]);
    return !Number.isNaN(value) && value < 100;
  }

  isFileActionEnabled(file: any): boolean {
    if (!file || file.isFailed) {
      return false;
    }

    return !!this.getFirstFailedFieldForFile(file);
  }

  isFileSuccessIndicatorVisible(file: any): boolean {
    if (!file || file.isFailed || this.isFileActionEnabled(file)) {
      return false;
    }

    const accuracy = Number(file.accuracy);
    return !Number.isNaN(accuracy) && accuracy >= 100;
  }

  private isMismatchValue(value: unknown): boolean {
    return String(value ?? '').trim().toUpperCase() === 'N';
  }

  private getFirstFailedFieldForFile(file: any): string | null {
    if (!file || file.isFailed) {
      return null;
    }

    for (const attr of this.matrixAttributes) {
      if (!this.shouldDisplayField(attr)) {
        continue;
      }

      if (this.isMismatchValue(file?.attributes?.[attr])) {
        return this.formatFieldName(attr);
      }
    }

    const normalizedFileName = this.normalizeFileKey(file.fileName || '');
    if (!normalizedFileName) {
      return null;
    }

    const failedDetail = this.validationDetails.find(detail => {
      return !detail.match && this.normalizeFileKey(detail.fileName || '') === normalizedFileName;
    });

    return failedDetail?.field ? this.formatFieldName(failedDetail.field) : null;
  }

  navigateToPromptTuningForFile(file: any, event?: MouseEvent): void {
    event?.stopPropagation();

    if (!this.authService.isAdmin) {
      return;
    }

    const model = this.documentCode || this.validationRun?.documentCode || '';
    if (!model || !file || file.isFailed) {
      return;
    }

    const firstFailedField = this.getFirstFailedFieldForFile(file);
    if (!firstFailedField) {
      return;
    }

    const rawFileName = file.fileName || '';
    const cleanFileName = this.getCleanFileName(rawFileName);
    const queryParams: Record<string, string> = {
      field: firstFailedField,
      fileName: cleanFileName,
      source: 'validation-matrix-tuning',
      returnLoanId: String(this.loanId),
      returnRunId: String(this.runId),
      returnDocumentCode: model,
      returnTab: this.activeTab || 'details'
    };

    const evaluationId = this.getEvaluationId();
    if (evaluationId && evaluationId !== 'Not Available' && evaluationId !== 'latest') {
      queryParams['returnEvaluationId'] = evaluationId;
    }

    const resolvedFileId = this.resolveEvaluationFileUuid(rawFileName || cleanFileName);
    if (resolvedFileId) {
      queryParams['fileId'] = resolvedFileId;
    }

    this.router.navigate(['/prompt-tuning', model], { queryParams });
  }

  navigateToPromptTuningForAttribute(attribute: string, event?: MouseEvent): void {
    event?.stopPropagation();

    if (!this.authService.isAdmin) {
      return;
    }

    const model = this.documentCode || this.validationRun?.documentCode || '';
    if (!model) {
      return;
    }

    const failedFile = this.getFailedFileForAttribute(attribute);
    if (!failedFile) {
      return;
    }

    const queryParams: Record<string, string> = {
      field: this.formatFieldName(attribute),
      fileName: this.getCleanFileName(failedFile.fileName),
      source: 'validation-matrix-tuning',
      returnLoanId: String(this.loanId),
      returnRunId: String(this.runId),
      returnDocumentCode: model,
      returnTab: this.activeTab || 'details'
    };

    const evaluationId = this.getEvaluationId();
    if (evaluationId && evaluationId !== 'Not Available' && evaluationId !== 'latest') {
      queryParams['returnEvaluationId'] = evaluationId;
    }

    const resolvedFileId = this.resolveEvaluationFileUuid(failedFile.fileName);
    if (resolvedFileId) {
      queryParams['fileId'] = resolvedFileId;
    }

    this.router.navigate(['/prompt-tuning', model], { queryParams });
  }

  getMismatchDetails(fileName: string, attribute: string): string {
    const detail = this.validationDetails.find(
      d => d.fileName === fileName && d.field === attribute && !d.match
    );
    
    if (detail) {
      return `Expected: ${detail.expectedValue}\nActual: ${detail.actualValue}`;
    }
    return '';
  }

  showTooltip(event: MouseEvent, fileName: string, attribute: string): void {
    // First check if validationDetails has any data
    if (this.validationDetails.length === 0) {
      this.tooltipContent = `Loading data...`;
      this.tooltipX = event.clientX + 10;
      this.tooltipY = event.clientY + 10;
      this.tooltipVisible = true;
      return;
    }
    
    // Try to find the exact mismatch detail
    const detail = this.validationDetails.find(
      d => d.fileName === fileName && 
           d.field === attribute && 
           !d.match
    );
    
    if (detail) {
      this.tooltipContent = `Expected: ${detail.expectedValue}\nActual: ${detail.actualValue}`;
      this.tooltipX = event.clientX + 10;
      this.tooltipY = event.clientY + 10;
      this.tooltipVisible = true;
    } else {
      // If exact match not found, show that details are in Results tab
      this.tooltipContent = `${attribute}\nSee Results tab for details`;
      this.tooltipX = event.clientX + 10;
      this.tooltipY = event.clientY + 10;
      this.tooltipVisible = true;
    }
  }

  hideTooltip(): void {
    this.tooltipVisible = false;
  }

  /**
   * Group validation details by file for file-based filtering
   */
  groupValidationDetailsByFile(): void {
    const fileGroupsMap = new Map<string, ValidationDetail[]>();
    
    // Group validation details by file name
    this.validationDetails.forEach(detail => {
      if (!fileGroupsMap.has(detail.fileName)) {
        fileGroupsMap.set(detail.fileName, []);
      }
      fileGroupsMap.get(detail.fileName)!.push(detail);
    });
    
    // Create file groups with accuracy calculations
    this.fileGroups = Array.from(fileGroupsMap.entries()).map(([fileName, fields]) => {
      const matchedFields = fields.filter(field => field.match).length;
      const totalFields = fields.length;
      const fileAccuracy = totalFields > 0 ? (matchedFields / totalFields) * 100 : 0;
      
      return {
        fileName,
        fileAccuracy,
        totalFields,
        matchedFields,
        fields
      };
    });
    
    // Sort file groups by file name
    this.fileGroups.sort((a, b) => a.fileName.localeCompare(b.fileName));
  }

  /**
   * Calculate duration from time_taken_seconds or from started_at/completed_at dates
   */
  calculateDuration(timeTakenSeconds: number | undefined | null, startedAt: string | undefined | null, completedAt: string | undefined | null): string {
    let totalSeconds = Math.floor(timeTakenSeconds || 0);
    
    // If time_taken_seconds is 0 or missing, try to calculate from dates
    if (totalSeconds === 0 && startedAt) {
      const startDate = new Date(startedAt.endsWith('Z') ? startedAt : startedAt + 'Z');
      
      if (completedAt) {
        const endDate = new Date(completedAt.endsWith('Z') ? completedAt : completedAt + 'Z');
        totalSeconds = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);
      } else {
        // No completed_at - calculate from start to now
        totalSeconds = Math.floor((Date.now() - startDate.getTime()) / 1000);
      }
    }
    
    // Format duration
    if (totalSeconds > 0) {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
    
    return '';
  }

  /**
   * Select a specific file group for filtering
   */
  selectFile(fileGroup: FileGroup): void {
    this.selectedFileGroup = fileGroup;
    this.filteredDetails = fileGroup.fields;
  }

  /**
   * Clear file selection and show all validation details
   */
  clearFileSelection(): void {
    this.selectedFileGroup = null;
    this.filteredDetails = this.validationDetails;
  }

  logout(): void {
    // Show logout confirmation modal (Scenario 3)
    this.modalService.showLogoutConfirmation(() => {
      this.authService.logout();
      this.isAuthenticated = false;
    });
  }

  navigateToStatsDashboard(): void {
    this.router.navigate(['/stats-dashboard']);
  }

  navigateToAdminSettings(): void {
    this.router.navigate(['/admin-settings']);
  }

  navigateToOperationsRequestAnalytics(): void {
    this.clientCapability.navigateToOpsHealth();
  }

}
